import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ArrowDown,
  BarChart3,
  Check,
  CheckCheck,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  CornerDownLeft,
  Forward,
  Info,
  LayoutGrid,
  Eye,
  Bell,
  MessageCircle,
  Mic,
  Moon,
  MoreHorizontal,
  Paperclip,
  Phone,
  Pin,
  Plus,
  Search,
  Send,
  Smile,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabaseClient';
import NewChatModal from '../components/chat/NewChatModal';
import UserProfileDialog from '../components/chat/UserProfileDialog';
import GroupInfoDialog from '../components/chat/GroupInfoDialog';
import CallPanel from '../components/chat/CallPanel';
import IncomingCallPopup from '../components/chat/IncomingCallPopup';
import DeleteMessageDialog from '../components/chat/DeleteMessageDialog';
import ClearConversationDialog from '../components/chat/ClearConversationDialog';
import MuteConversationDialog from '../components/chat/MuteConversationDialog';
import EmojiPickerPopover from '../components/chat/EmojiPickerPopover';
import FileUploadModal from '../components/chat/FileUploadModal';
import AudioPlayer, { VoiceRecordingBar } from '../components/chat/AudioPlayer';
import TwemojiText, { ReactionEmoji } from '../components/TwemojiText';
import usePresence, { formatLastSeen } from '../hooks/usePresence';
import useChatCalls from '../hooks/useChatCalls';
import TypingIndicator from '../components/chat/TypingIndicator';
import MessageReactionMoreButton from '../components/chat/MessageReactionMoreButton';
import MessageReplyQuote from '../components/chat/MessageReplyQuote';
import MessageReadReceipt from '../components/chat/MessageReadReceipt';
import ForwardMessageModal from '../components/chat/ForwardMessageModal';
import MessageActionMenu from '../components/chat/MessageActionMenu';
import ChatPollDialog, { PollMessage } from '../components/chat/ChatPollDialog';
import { ContactMessage } from '../components/chat/ContactShareDialog';
import ChatModalPortal from '../components/chat/ChatModalPortal';
import { subscribeToPush, unsubscribeFromPush } from '../lib/pushNotifications';

const QUICK_REACTIONS = ['👍', '❤️', '🔥', '😂', '👏', '🙏'];

const readMutedChatExpiries = (userId) => {
  try {
    return JSON.parse(localStorage.getItem(`muted_chat_expiries_${userId || 'default'}`) || '{}');
  } catch (error) {
    console.error('Unable to load muted chat expiry times:', error);
    return {};
  }
};

const readActiveMutedChatIds = (userId) => {
  try {
    const chatIds = JSON.parse(localStorage.getItem(`muted_chats_${userId || 'default'}`) || '[]');
    const expiries = readMutedChatExpiries(userId);
    return chatIds.filter((chatId) => (
      !expiries[chatId] || new Date(expiries[chatId]).getTime() > Date.now()
    ));
  } catch (error) {
    console.error('Unable to load muted chats:', error);
    return [];
  }
};

const formatMessageTime = (isoString, language) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  if (language === 'de') {
    const hours = date.getHours();
    const hour = hours % 12 || 12;
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const period = hours < 12 ? 'VM' : 'NM';
    return `${hour}:${minutes} ${period}`;
  }
  const hours = date.getHours();
  const hour = hours % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const period = hours < 12 ? 'PD' : 'MD';
  return `${hour}:${minutes} ${period}`;
};

const formatReadDate = (isoString, language) => {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  if (language === 'de') {
    const now = new Date();
    const dateLabel = date.toDateString() === now.toDateString()
      ? 'heute'
      : new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(date);
    return `Zuletzt gesehen: ${dateLabel} um ${formatMessageTime(isoString, language)}`;
  }
  const now = new Date();
  if (
    date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate()
  ) {
    return `Parë Sot, ${formatMessageTime(isoString, language)}`;
  }
  const month = date.toLocaleDateString('sq-AL', { month: 'short' });
  const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
  return `Parë ${capitalizedMonth} ${date.getDate()}, ${formatMessageTime(isoString, language)}`;
};

const getDisplayName = (profile, fallback = 'Student') => (
  [profile?.first_name || profile?.firstName, profile?.last_name || profile?.lastName]
    .filter(Boolean)
    .join(' ')
  || fallback
);

const getConversationSnippet = (message, fallback = 'Bisedë e re') => {
  if (!message) return fallback;
  if (message.message_type === 'audio' || message.attachment_type?.startsWith('audio/')) {
    return 'Mesazh audio';
  }
  if (typeof message.content === 'string' && message.content.startsWith('{')) {
    try {
      if (JSON.parse(message.content)?.type === 'audio') return 'Mesazh audio';
    } catch (error) {
      // Keep malformed or non-audio message content visible as text.
    }
  }
  return message.content || fallback;
};

const ProfileAvatar = ({ profile, name, size = 'h-9 w-9', rounded = 'rounded-full' }) => (
  <div className={`relative flex ${size} shrink-0 items-center justify-center overflow-hidden ${rounded} ${profile?.avatar_url || profile?.avatarUrl ? 'bg-transparent' : 'bg-zinc-700 text-xs font-bold text-white'}`}>
    {profile?.avatar_url || profile?.avatarUrl ? (
      <img
        src={profile.avatar_url || profile.avatarUrl}
        alt=""
        className="h-full w-full bg-transparent object-contain"
      />
    ) : (
      getInitials(name)
    )}
  </div>
);

const formatMessageDateGroup = (isoString, language) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  if (language === 'de') {
    if (date.toDateString() === now.toDateString()) return 'Heute';
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Gestern';
    return new Intl.DateTimeFormat('de-DE', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }
  if (date.toDateString() === now.toDateString()) return 'Sot';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Dje';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatConversationListTime = (isoString, language) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  return date.toDateString() === new Date().toDateString()
    ? formatMessageTime(isoString, language)
    : formatMessageDateGroup(isoString, language);
};

const getInitials = (name) => {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const DEFAULT_NOTES_MESSAGES = [];

const ChatPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [profilesLoadError, setProfilesLoadError] = useState('');
  const [directChatError, setDirectChatError] = useState('');
  const [conversations, setConversations] = useState([]);
  const callSession = useChatCalls({ user, conversations });
  const { incomingCall, activeCall } = callSession;
  const startChatCall = callSession.startCall;
  const endChatCall = callSession.endCall;
  const [notesConversationId, setNotesConversationId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [search, setSearch] = useState('');
  const chatDraftsStorageKey = `chat_drafts_${user?.id || 'default'}`;
  const [chatDrafts, setChatDrafts] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(chatDraftsStorageKey) || '{}');
    } catch {
      return {};
    }
  });
  const [hiddenMessageIds, setHiddenMessageIds] = useState([]);
  const [scheduledMessageHides, setScheduledMessageHides] = useState([]);
  const [hideMessagesError, setHideMessagesError] = useState('');

  // My Notes local storage
  const notesStorageKey = `chat_notes_${user?.id || 'default'}`;
  const notesHideStorageKey = `chat_notes_hides_${user?.id || 'default'}`;
  const [notesMessages, setNotesMessages] = useState(() => {
    try {
      const stored = localStorage.getItem(notesStorageKey);
      return stored ? JSON.parse(stored) : DEFAULT_NOTES_MESSAGES;
    } catch {
      return DEFAULT_NOTES_MESSAGES;
    }
  });
  const [scheduledNoteHides, setScheduledNoteHides] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(notesHideStorageKey) || '[]');
    } catch (error) {
      console.warn('Could not load scheduled note hides:', error);
      return [];
    }
  });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(notesStorageKey);
      setNotesMessages(stored ? JSON.parse(stored) : DEFAULT_NOTES_MESSAGES);
    } catch (error) {
      console.warn('Could not load notes locally:', error);
      setNotesMessages(DEFAULT_NOTES_MESSAGES);
    }
  }, [notesStorageKey]);

  useEffect(() => {
    try {
      setScheduledNoteHides(JSON.parse(localStorage.getItem(notesHideStorageKey) || '[]'));
    } catch (error) {
      console.warn('Could not load scheduled note hides:', error);
      setScheduledNoteHides([]);
    }
  }, [notesHideStorageKey]);

  useEffect(() => {
    if (!user?.id) return;
    try {
      localStorage.removeItem(`hidden_chat_messages_${user.id}`);
      localStorage.removeItem('hidden_chat_messages_default');
    } catch (error) {
      console.warn('Could not remove legacy locally hidden messages:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    try {
      setChatDrafts(JSON.parse(localStorage.getItem(chatDraftsStorageKey) || '{}'));
    } catch (error) {
      console.warn('Could not load chat drafts:', error);
      setChatDrafts({});
    }
  }, [chatDraftsStorageKey]);

  const saveNotesMessages = useCallback((newMsgs) => {
    setNotesMessages(newMsgs);
    try {
      localStorage.setItem(notesStorageKey, JSON.stringify(newMsgs));
    } catch (e) {
      console.warn('Could not save notes locally:', e);
    }
  }, [notesStorageKey]);

  const saveScheduledNoteHides = useCallback((rows) => {
    setScheduledNoteHides(rows);
    try {
      localStorage.setItem(notesHideStorageKey, JSON.stringify(rows));
      return true;
    } catch (error) {
      console.warn('Could not save scheduled note hides:', error);
      setHideMessagesError('Fshirja e planifikuar e shënimeve nuk u ruajt.');
      return false;
    }
  }, [notesHideStorageKey]);

  const hideNotesForMe = (messagesToHide, hideAfterSeconds = null) => {
    const ids = new Set(messagesToHide.map((message) => String(message.id)));
    if (!ids.size) return true;
    const remainingHides = scheduledNoteHides.filter(
      (row) => !ids.has(String(row.message_id))
    );

    if (hideAfterSeconds === null) {
      const schedulesSaved = saveScheduledNoteHides(remainingHides);
      if (!schedulesSaved) return false;
      const updated = notesMessages.filter((message) => !ids.has(String(message.id)));
      saveNotesMessages(updated);
      if (selected?.kind === 'notes') setMessages(updated);
      return true;
    }

    const hideAt = new Date(Date.now() + hideAfterSeconds * 1000).toISOString();
    const scheduled = [
      ...remainingHides,
      ...[...ids].map((messageId) => ({ message_id: messageId, hide_at: hideAt })),
    ];
    return saveScheduledNoteHides(scheduled);
  };

  useEffect(() => {
    if (!scheduledNoteHides.length) return undefined;
    const nextExpiry = Math.min(...scheduledNoteHides.map((row) => new Date(row.hide_at).getTime()));
    const timeout = window.setTimeout(() => {
      const now = Date.now();
      const expired = scheduledNoteHides.filter((row) => new Date(row.hide_at).getTime() <= now);
      if (!expired.length) return;

      const expiredIds = new Set(expired.map((row) => String(row.message_id)));
      const updated = notesMessages.filter((message) => !expiredIds.has(String(message.id)));
      saveNotesMessages(updated);
      if (selected?.kind === 'notes') setMessages(updated);
      saveScheduledNoteHides(scheduledNoteHides.filter((row) => new Date(row.hide_at).getTime() > now));
    }, Math.max(0, nextExpiry - Date.now()) + 25);
    return () => window.clearTimeout(timeout);
  }, [scheduledNoteHides, notesMessages, selected?.kind, saveNotesMessages, saveScheduledNoteHides]);

  const setCurrentDraft = (value) => {
    setNewMessage(value);
    if (!selected?.id) return;
    setChatDrafts((previous) => {
      const next = { ...previous };
      if (value.trim()) next[selected.id] = value;
      else delete next[selected.id];
      try {
        localStorage.setItem(chatDraftsStorageKey, JSON.stringify(next));
      } catch (error) {
        console.error('Unable to save chat draft:', error);
      }
      return next;
    });
  };

  const resizeComposer = (textarea) => {
    if (!textarea) return;
    const maxHeight = 104;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  useEffect(() => {
    resizeComposer(composerInputRef.current);
  }, [newMessage]);

  const toggleChatNotifications = async () => {
    setNotificationPermissionError('');
    if (notificationsEnabled) {
      try {
        await unsubscribeFromPush(user?.id);
      } catch (e) {
        console.warn('Unsubscribe error:', e);
      }
      localStorage.setItem('app_desktop_notifications', 'false');
      setNotificationsEnabled(false);
      return;
    }
    if (!('Notification' in window)) {
      setNotificationPermissionError('Ky shfletues nuk i mbështet njoftimet në desktop.');
      return;
    }

    try {
      const permission = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
      if (permission !== 'granted') {
        setNotificationPermissionError('Lejo njoftimet në shfletues për t’i aktivizuar.');
        return;
      }
      try {
        await subscribeToPush(user?.id);
      } catch (pushErr) {
        console.warn('Push subscription failed, continuing with desktop notification mode:', pushErr);
      }
      localStorage.setItem('app_desktop_notifications', 'true');
      setNotificationsEnabled(true);
    } catch (error) {
      console.error('Unable to enable chat notifications:', error);
      setNotificationPermissionError('Nuk u arrit të aktivizohen njoftimet.');
    }
  };

  const [chatMessagesMap, setChatMessagesMap] = useState(() => {
    return {};
  });
  const chatMessagesMapRef = useRef(chatMessagesMap);
  chatMessagesMapRef.current = chatMessagesMap;
  const [messageReadsMap, setMessageReadsMap] = useState({});

  const saveChatMessagesMap = (newMap) => setChatMessagesMap(newMap);

  // Unread counts tracking
  const [unreadMap, setUnreadMap] = useState({});

  // Mobile layout switch
  const [mobileShowList, setMobileShowList] = useState(true);

  // Presence & Typing hook (matching entriks-hr)
  const {
    onlineUserIds,
    isUserOnline,
    isConversationTyping,
    sendTyping,
  } = usePresence(user, selected?.id);

  // Modals state
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showGroupInfoDialog, setShowGroupInfoDialog] = useState(false);
  const [showUserProfileDialog, setShowUserProfileDialog] = useState(false);
  const [showCallPanel, setShowCallPanel] = useState(false);
  const [showDeleteMessageDialog, setShowDeleteMessageDialog] = useState(false);
  const [showClearConversationDialog, setShowClearConversationDialog] = useState(false);
  const [targetDeleteMessages, setTargetDeleteMessages] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardingMessage, setForwardingMessage] = useState(null);

  // Replying quote state
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);

  // In-chat search state
  const [showInChatSearch, setShowInChatSearch] = useState(false);
  const [inChatSearchQuery, setInChatSearchQuery] = useState('');
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [mediaGalleryFilter, setMediaGalleryFilter] = useState('all');
  const [showChatSettings, setShowChatSettings] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    () => localStorage.getItem('app_desktop_notifications') === 'true'
  );
  const [notificationPermissionError, setNotificationPermissionError] = useState('');
  const [chatFilter, setChatFilter] = useState('all');
  const [showChatFilterMenu, setShowChatFilterMenu] = useState(false);
  const chatFilterMenuRef = useRef(null);

  useEffect(() => {
    if (!showChatFilterMenu) return undefined;
    const handleOutsideClick = (e) => {
      if (chatFilterMenuRef.current && !chatFilterMenuRef.current.contains(e.target)) {
        setShowChatFilterMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showChatFilterMenu]);
  const [openChatMenuId, setOpenChatMenuId] = useState(null);
  const [dismissedPinnedBannerChatId, setDismissedPinnedBannerChatId] = useState(null);
  const [pinnedMessageIndex, setPinnedMessageIndex] = useState(0);
  const [messagePinError, setMessagePinError] = useState('');
  const [pinnedChatIds, setPinnedChatIds] = useState([]);
  const [conversationPinError, setConversationPinError] = useState('');
  const [pinningChatId, setPinningChatId] = useState(null);
  const [muteTargetChat, setMuteTargetChat] = useState(null);
  const mutedChatsStorageKey = `muted_chats_${user?.id || 'default'}`;
  const mutedChatExpiriesStorageKey = `muted_chat_expiries_${user?.id || 'default'}`;
  const [mutedChatIds, setMutedChatIds] = useState(() => readActiveMutedChatIds(user?.id));
  const [mutedChatExpiries, setMutedChatExpiries] = useState(() => readMutedChatExpiries(user?.id));
  const [hiddenChatIds, setHiddenChatIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`hidden_chats_${user?.id || 'default'}`) || '[]');
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!user?.id) {
      setPinnedChatIds([]);
      return undefined;
    }
    let isCurrent = true;

    const loadPinnedChats = async () => {
      setConversationPinError('');
      try {
        const { data: pinRows, error: pinLoadError } = await supabase
          .from('conversation_pins')
          .select('conversation_id')
          .eq('user_id', user.id);
        if (pinLoadError) throw pinLoadError;

        let loadedIds = (pinRows || []).map((row) => row.conversation_id);
        if (isCurrent) setPinnedChatIds(loadedIds);

        const legacyKey = `pinned_chats_${user.id}`;
        const legacyPins = JSON.parse(localStorage.getItem(legacyKey) || '[]');
        if (Array.isArray(legacyPins) && legacyPins.length) {
          const { data: memberRows, error: membershipError } = await supabase
            .from('conversation_members')
            .select('conversation_id')
            .eq('user_id', user.id)
            .in('conversation_id', legacyPins);
          if (membershipError) throw membershipError;

          const memberIds = (memberRows || []).map((row) => row.conversation_id);
          const pinsToMigrate = memberIds
            .filter((conversationId) => !loadedIds.includes(conversationId))
            .map((conversation_id) => ({ conversation_id, user_id: user.id }));
          if (pinsToMigrate.length) {
            const { error: migrationError } = await supabase
              .from('conversation_pins')
              .upsert(pinsToMigrate, {
                onConflict: 'conversation_id,user_id',
                ignoreDuplicates: true,
              });
            if (migrationError) throw migrationError;
          }

          loadedIds = [...new Set([...loadedIds, ...memberIds])];
          localStorage.removeItem(legacyKey);
        } else {
          localStorage.removeItem(legacyKey);
        }

        if (isCurrent) setPinnedChatIds(loadedIds);
      } catch (error) {
        console.error('Unable to load pinned conversations from Supabase:', error);
        if (isCurrent) {
          setConversationPinError('Bisedat e fiksuara nuk u ngarkuan. Kontrollo përditësimin e Supabase.');
        }
      }
    };

    loadPinnedChats();
    return () => {
      isCurrent = false;
    };
  }, [user?.id]);

  // MediaRecorder Voice Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingStream, setRecordingStream] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  // Reactions Map
  const [reactionsMap, setReactionsMap] = useState({});
  const [pollVotesMap, setPollVotesMap] = useState({});
  const [pollActionError, setPollActionError] = useState('');
  const [showPollDialog, setShowPollDialog] = useState(false);
  const [isCreatingPoll, setIsCreatingPoll] = useState(false);
  const [votingOnPollId, setVotingOnPollId] = useState(null);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [composerScrollTop, setComposerScrollTop] = useState(0);
  const composerInputRef = useRef(null);
  const emojiButtonRef = useRef(null);

  const messagesContainerRef = useRef(null);
  const atMessagesBottomRef = useRef(true);
  const [isAtMessagesBottom, setIsAtMessagesBottom] = useState(true);
  const previousLatestMessageRef = useRef({ conversationId: selected?.id, messageId: null });
  const pendingOlderMessagesScrollRef = useRef(null);
  const [messageWindow, setMessageWindow] = useState({ conversationId: null, count: 20 });
  const selectedConversationIdRef = useRef(selected?.id);
  selectedConversationIdRef.current = selected?.id;

  const loadChatData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [
        { data: membershipRows, error: membershipError },
      ] = await Promise.all([
        supabase.from('conversation_members').select('conversation_id, user_id').eq('user_id', user.id),
      ]);

      if (membershipError) throw membershipError;

      let memberships = membershipRows || [];
      let conversationIds = memberships.map((row) => row.conversation_id);

      let { data: profileRows, error: profileError } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, profession, avatar_url, email, last_seen_at')
        .order('username', { ascending: true });

      const emailColumnMissing = profileError
        && ['42703', 'PGRST204'].includes(profileError.code)
        && /email/i.test(profileError.message || '');
      if (emailColumnMissing) {
        console.warn(
          'The profiles.email column is missing. Chat profiles will load without email until supabase_schema.sql is applied.',
          profileError
        );
        const fallbackResult = await supabase
          .from('profiles')
          .select('id, username, first_name, last_name, profession, avatar_url, last_seen_at')
          .order('username', { ascending: true });
        profileRows = fallbackResult.data;
        profileError = fallbackResult.error;
      }

      if (profileError) {
        setProfilesLoadError('Nuk mund të ngarkohen profilet. Kontrolloni lidhjen dhe politikat Supabase.');
        throw profileError;
      }
      setProfiles(profileRows || []);
      setProfilesLoadError('');
      if (membershipError) throw membershipError;

      // Handle My Notes
      const { data: existingNotes, error: notesError } = await supabase
        .from('conversations')
        .select('id, kind, name, created_by, created_at')
        .eq('kind', 'notes')
        .eq('created_by', user.id)
        .limit(1);
      if (notesError) throw notesError;
      let notesConversation = existingNotes?.[0];
      if (!notesConversation) {
        const { data: createdNotes, error: createNotesError } = await supabase
          .from('conversations')
          .insert({ kind: 'notes', name: 'My Notes', created_by: user.id })
          .select('id, kind, name, created_by, created_at')
          .single();
        if (createNotesError) throw createNotesError;
        notesConversation = createdNotes;
        const { error: notesMemberError } = await supabase.from('conversation_members').insert({
          conversation_id: notesConversation.id,
          user_id: user.id,
        });
        if (notesMemberError) throw notesMemberError;
      }
      setNotesConversationId(notesConversation.id);

      if (!conversationIds.includes(notesConversation.id)) {
        memberships = [...memberships, { conversation_id: notesConversation.id, user_id: user.id }];
        conversationIds = [...conversationIds, notesConversation.id];
      }

      const { data: hiddenRows, error: hiddenError } = await supabase
        .from('message_hides')
        .select('message_id, hide_at')
        .eq('user_id', user.id);
      if (hiddenError) {
        console.error('Unable to load hidden chat messages:', hiddenError);
        setHideMessagesError('Fshirja personale kërkon skemën e fundit Supabase.');
      } else {
        const now = Date.now();
        const hideRows = hiddenRows || [];
        const expiredHideRows = hideRows.filter((row) => row.hide_at && new Date(row.hide_at).getTime() <= now);
        setHiddenMessageIds(hideRows
          .filter((row) => !row.hide_at || new Date(row.hide_at).getTime() <= now)
          .map((row) => String(row.message_id)));
        setScheduledMessageHides(hideRows.filter((row) => row.hide_at && new Date(row.hide_at).getTime() > now));
        setHideMessagesError('');
        const expiredResults = await Promise.allSettled(expiredHideRows.map((row) => (
          supabase.rpc('hide_message_for_me', {
            p_message_id: row.message_id,
            p_hide_after_seconds: null,
          })
        )));
        expiredResults.forEach((result) => {
          if (result.status === 'rejected' || result.value.error) {
            console.error('Unable to finalize an expired message hide:', result.status === 'rejected' ? result.reason : result.value.error);
            setHideMessagesError('Disa fshirje të planifikuara nuk u përditësuan në server.');
          }
        });
      }

      // If no conversations (except notes), return early
      if (conversationIds.length === 1 && conversationIds[0] === notesConversation.id) {
        setProfiles(profileRows || []);
        setConversations([]);
        setChatMessagesMap({});
        setUnreadMap({});
        return;
      }

      const [{ data: conversationRows, error: conversationError }, { data: memberRows, error: memberError }, { data: messageRows, error: messageError }] = await Promise.all([
        supabase.from('conversations').select('id, kind, name, created_by, created_at').in('id', conversationIds).order('created_at', { ascending: true }),
        supabase.from('conversation_members').select('conversation_id, user_id').in('conversation_id', conversationIds),
        supabase.from('messages').select('id, conversation_id, sender_id, sender_name, content, timestamp, message_type, reply_to_id, attachment_url, attachment_name, attachment_type, attachment_size, edited_at, pinned_at, forwarded').in('conversation_id', conversationIds).order('timestamp', { ascending: true }),
      ]);

      if (conversationError) throw conversationError;
      if (memberError) throw memberError;
      if (messageError) throw messageError;
      const messageIds = (messageRows || []).map((message) => message.id);
      const { data: readRows, error: readsError } = messageIds.length
        ? await supabase.from('message_reads').select('message_id, user_id, read_at').in('message_id', messageIds)
        : { data: [], error: null };
      if (readsError) throw readsError;
      const readsByMessage = (readRows || []).reduce((result, read) => {
        result[read.message_id] = [...(result[read.message_id] || []), read];
        return result;
      }, {});
      const { data: reactionRows, error: reactionsError } = messageIds.length
        ? await supabase.from('message_reactions').select('message_id, user_id, emoji').in('message_id', messageIds)
        : { data: [], error: null };
      if (reactionsError) throw reactionsError;
      const reactionsByMessage = (reactionRows || []).reduce((result, reaction) => {
        result[reaction.message_id] = [...(result[reaction.message_id] || []), reaction];
        return result;
      }, {});
      const pollMessageIds = (messageRows || [])
        .filter((message) => message.message_type === 'poll')
        .map((message) => message.id);
      const { data: pollVoteRows, error: pollVotesError } = pollMessageIds.length
        ? await supabase.from('message_poll_votes')
          .select('message_id, user_id, option_index')
          .in('message_id', pollMessageIds)
        : { data: [], error: null };
      if (pollVotesError) {
        console.error('Unable to load poll votes:', pollVotesError);
        setPollActionError('Anketat kërkojnë skemën e fundit Supabase për votat.');
      }
      const votesByMessage = (pollVoteRows || []).reduce((result, vote) => {
        result[vote.message_id] = [...(result[vote.message_id] || []), vote];
        return result;
      }, {});

      const membersByConversation = (memberRows || []).reduce((result, row) => {
        result[row.conversation_id] = [...(result[row.conversation_id] || []), row.user_id];
        return result;
      }, {});
      const profileById = (profileRows || []).reduce((result, profile) => {
        result[profile.id] = profile;
        return result;
      }, {});
      const messagesByConversation = (messageRows || []).reduce((result, message) => {
        const mapped = {
          ...message,
          created_at: message.timestamp,
          senderName: getDisplayName(profileById[message.sender_id]),
          seen: message.sender_id !== user.id
            ? false
            : (readsByMessage[message.id] || []).some((read) => read.user_id !== user.id),
          reactions: reactionsByMessage[message.id] || [],
        };
        result[message.conversation_id] = [...(result[message.conversation_id] || []), mapped];
        return result;
      }, {});
      const hiddenMessageIdSet = new Set((hiddenRows || [])
        .filter((row) => !row.hide_at || new Date(row.hide_at).getTime() <= Date.now())
        .map((row) => String(row.message_id)));
      const unreadCountsByConversation = {};
      Object.entries(messagesByConversation).forEach(([conversationId, conversationMessages]) => {
        unreadCountsByConversation[conversationId] = conversationMessages.filter((message) => (
          message.sender_id !== user.id
          && !hiddenMessageIdSet.has(String(message.id))
          && !(readsByMessage[message.id] || []).some((read) => read.user_id === user.id)
        )).length;
      });
      const messageById = Object.values(messagesByConversation).flat().reduce((result, message) => {
        result[message.id] = message;
        return result;
      }, {});
      Object.keys(messagesByConversation).forEach((conversationId) => {
        messagesByConversation[conversationId] = messagesByConversation[conversationId].map((message) => ({
          ...message,
          replyTo: message.reply_to_id ? messageById[message.reply_to_id] || null : null,
        }));
      });
      const mappedConversations = (conversationRows || [])
        .filter((conversation) => conversation.kind === 'direct' || conversation.kind === 'group')
        .map((conversation) => {
        const ids = membersByConversation[conversation.id] || [];
        const peerId = ids.find((id) => id !== user.id);
        const peer = profileById[peerId] || null;
        const conversationMessages = messagesByConversation[conversation.id] || [];
        const latestMessage = conversationMessages[conversationMessages.length - 1];
        const title = conversation.kind === 'group'
          ? conversation.name || 'Grup mësimor'
          : getDisplayName(peer, 'Përdorues');
        return {
          ...conversation,
          title,
          peer: conversation.kind === 'direct' ? peer : null,
          memberIds: ids,
          members: ids.map((id) => profileById[id]).filter(Boolean),
          snippet: getConversationSnippet(latestMessage),
          time: latestMessage?.created_at
            ? formatConversationListTime(latestMessage.created_at, language)
            : 'Tani',
          unread: unreadCountsByConversation[conversation.id] || 0,
        };
      });

      setProfiles(profileRows || []);
      setConversations(mappedConversations);
      setChatMessagesMap(messagesByConversation);
      setUnreadMap(unreadCountsByConversation);
      setMessageReadsMap(readsByMessage);
      setReactionsMap(reactionsByMessage);
      setPollVotesMap(votesByMessage);
      if (notesConversation?.id && messagesByConversation[notesConversation.id]) {
        setNotesMessages(messagesByConversation[notesConversation.id]);
      }
    } catch (error) {
      console.error('Unable to load chat data:', error);
    }
  }, [user?.id, language]);

  const handleStartCall = useCallback(async () => {
    if (!selected || selected.kind === 'notes' || !user?.id) return;
    setShowCallPanel(true);
    await startChatCall(selected);
  }, [selected, startChatCall, user?.id]);

  const handleEndCall = useCallback(() => {
    setShowCallPanel(false);
    void endChatCall();
  }, [endChatCall]);

  const handleCreateGroup = useCallback(async ({ name, memberIds }) => {
    if (!user?.id || !name || !memberIds.length) return false;

    const { data: group, error: groupError } = await supabase
      .from('conversations')
      .insert({ kind: 'group', name, created_by: user.id })
      .select('id, kind, name, created_by, created_at')
      .single();
    if (groupError) {
      console.error('Unable to create group conversation:', groupError);
      return false;
    }

    const allMemberIds = [...new Set([user.id, ...memberIds])];
    const { error: membersError } = await supabase.from('conversation_members').insert(
      allMemberIds.map((userId) => ({ conversation_id: group.id, user_id: userId }))
    );
    if (membersError) {
      console.error('Unable to add group conversation members:', membersError);
      return false;
    }

    await loadChatData();
    setSelected({
      ...group,
      title: group.name,
      peer: null,
      memberIds: allMemberIds,
      members: allMemberIds.map((id) => profiles.find((profile) => profile.id === id)).filter(Boolean),
      snippet: 'Grupi u krijua',
      time: 'Tani',
      unread: 0,
    });
    setMobileShowList(false);
    return true;
  }, [loadChatData, profiles, user?.id]);

  const handleAddGroupMember = useCallback(async (memberId) => {
    if (!selected || selected.kind !== 'group') return false;
    const { error } = await supabase.from('conversation_members').insert({
      conversation_id: selected.id,
      user_id: memberId,
    });
    if (error) {
      console.error('Unable to add group member:', error);
      throw error;
    }

    await loadChatData();
    return true;
  }, [loadChatData, selected]);

  const handleLeaveGroup = useCallback(async () => {
    if (!selected || selected.kind !== 'group' || !user?.id) return false;
    const { error } = await supabase
      .from('conversation_members')
      .delete()
      .eq('conversation_id', selected.id)
      .eq('user_id', user.id);
    if (error) {
      console.error('Unable to leave group:', error);
      throw error;
    }
    setShowGroupInfoDialog(false);
    setSelected(null);
    setMobileShowList(true);
    await loadChatData();
    return true;
  }, [loadChatData, selected, user?.id]);

  const handleStartDirectChat = async (targetUser) => {
    if (!user?.id || !targetUser?.id) return false;
    setDirectChatError('');
    if (targetUser.id === user.id) {
      setSelected(myNotesChat);
      setMobileShowList(false);
      return true;
    }
    const { data: ownMemberships, error: ownMembershipError } = await supabase
      .from('conversation_members')
      .select('conversation_id')
      .eq('user_id', user.id);
    if (ownMembershipError) {
      console.error('Unable to find direct chats:', ownMembershipError);
      setDirectChatError('Nuk mund të hapet biseda. Provoni përsëri.');
      return false;
    }
    const candidateIds = (ownMemberships || []).map((row) => row.conversation_id);
    let conversation = null;
    if (candidateIds.length) {
      const { data: peerMemberships, error: peerMembershipError } = await supabase
        .from('conversation_members')
        .select('conversation_id')
        .eq('user_id', targetUser.id)
        .in('conversation_id', candidateIds);
      if (peerMembershipError) {
        console.error('Unable to find existing direct chat:', peerMembershipError);
        setDirectChatError('Nuk mund të hapet biseda ekzistuese.');
        return false;
      }
      const sharedIds = (peerMemberships || []).map((row) => row.conversation_id);
      if (sharedIds.length) {
        const { data: directRows, error: directError } = await supabase
          .from('conversations')
          .select('id, kind, name, created_by, created_at')
          .eq('kind', 'direct')
          .in('id', sharedIds)
          .limit(1);
        if (directError) {
          console.error('Unable to load existing direct chat:', directError);
          setDirectChatError('Nuk mund të ngarkohen bisedat ekzistuese.');
          return false;
        }
        conversation = directRows?.[0] || null;
      }
    }
    if (!conversation) {
      const { data: createdConversation, error: conversationError } = await supabase
        .from('conversations')
        .insert({ kind: 'direct', created_by: user.id })
        .select('id, kind, name, created_by, created_at')
        .single();
      if (conversationError) {
        console.error('Unable to create direct chat:', conversationError);
        setDirectChatError('Nuk mund të krijohet biseda. Kontrolloni Supabase.');
        return false;
      }
      const { error: memberError } = await supabase.from('conversation_members').insert([
        { conversation_id: createdConversation.id, user_id: user.id },
        { conversation_id: createdConversation.id, user_id: targetUser.id },
      ]);
      if (memberError) {
        console.error('Unable to add direct chat members:', memberError);
        setDirectChatError('Biseda u krijua, por anëtari nuk mund të shtohej.');
        return false;
      }
      conversation = createdConversation;
    }
    await loadChatData();
    setSelected({
      ...conversation,
      title: getDisplayName(targetUser, 'Përdorues'),
      peer: targetUser,
      snippet: 'Bisedë e re',
      time: 'Tani',
      unread: 0,
    });
    setMobileShowList(false);
    return true;
  };

  useEffect(() => {
    loadChatData();
  }, [loadChatData]);

  useEffect(() => {
    if (!user?.id) return undefined;
    const channel = supabase
      .channel(`german-chat-messages-${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        const message = payload.new;
        const conversationMessages = chatMessagesMapRef.current[message.conversation_id] || [];
        if (conversationMessages.some((item) => item.id === message.id)) return;
        const mapped = {
          ...message,
          created_at: message.timestamp,
          senderName: message.sender_name,
          seen: false,
        };
        const replyTo = conversationMessages.find((item) => item.id === message.reply_to_id) || null;
        const nextMessages = [...conversationMessages, { ...mapped, replyTo }];
        chatMessagesMapRef.current = {
          ...chatMessagesMapRef.current,
          [message.conversation_id]: nextMessages,
        };
        setChatMessagesMap(chatMessagesMapRef.current);
        if (message.sender_id !== user.id) {
          if (
            selectedConversationIdRef.current === message.conversation_id &&
            document.hasFocus?.() &&
            document.visibilityState === 'visible'
          ) {
            // User is actively reading this conversation: mark as read immediately in Supabase
            supabase.from('message_reads').upsert(
              { message_id: message.id, user_id: user.id, read_at: new Date().toISOString() },
              { onConflict: 'message_id,user_id' }
            );
          } else {
            setUnreadMap((previous) => ({
              ...previous,
              [message.conversation_id]: (previous[message.conversation_id] || 0) + 1,
            }));
          }
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages' }, (payload) => {
        const message = payload.new;
        setChatMessagesMap((previous) => ({
          ...previous,
          [message.conversation_id]: (previous[message.conversation_id] || []).map((item) =>
            item.id === message.id
              ? { ...item, ...message, created_at: message.timestamp, senderName: message.sender_name }
              : item
          ),
        }));
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'messages' }, (payload) => {
        const messageId = payload.old?.id;
        if (!messageId) return;
        setChatMessagesMap((previous) => Object.fromEntries(
          Object.entries(previous).map(([conversationId, rows]) => [
            conversationId,
            rows.filter((message) => message.id !== messageId),
          ])
        ));
        setMessages((previous) => previous.filter((message) => message.id !== messageId));
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_reads' }, (payload) => {
        const read = payload.new;
        if (!read?.message_id) return;
        setMessageReadsMap((previous) => {
          const current = previous[read.message_id] || [];
          if (current.some((item) => item.user_id === read.user_id)) {
            return {
              ...previous,
              [read.message_id]: current.map((item) => item.user_id === read.user_id ? read : item),
            };
          }
          return { ...previous, [read.message_id]: [...current, read] };
        });
        if (read.user_id === user.id) {
          loadChatData();
        }
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_hides', filter: `user_id=eq.${user.id}` }, (payload) => {
        const row = payload.new || payload.old;
        const messageId = String(row?.message_id || '');
        if (!messageId) return;
        if (payload.eventType !== 'DELETE' && row.hide_at && new Date(row.hide_at).getTime() > Date.now()) {
          setScheduledMessageHides((previous) => [
            ...previous.filter((item) => String(item.message_id) !== messageId),
            row,
          ]);
          return;
        }
        setScheduledMessageHides((previous) => previous.filter((item) => String(item.message_id) !== messageId));
        setHiddenMessageIds((previous) => (
          payload.eventType === 'DELETE'
            ? previous.filter((id) => id !== messageId)
            : previous.includes(messageId) ? previous : [...previous, messageId]
        ));
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_reactions' }, (payload) => {
        const reaction = payload.new || payload.old;
        if (!reaction?.message_id) return;
        setReactionsMap((previous) => {
          const current = (previous[reaction.message_id] || []).filter(
            (item) => !(item.user_id === reaction.user_id)
          );
          return {
            ...previous,
            [reaction.message_id]: payload.eventType === 'DELETE'
              ? current
              : [...current, reaction],
          };
        });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_poll_votes' }, (payload) => {
        const vote = payload.new || payload.old;
        if (!vote?.message_id || !vote?.user_id) return;
        setPollVotesMap((previous) => {
          const current = (previous[vote.message_id] || []).filter((item) => item.user_id !== vote.user_id);
          return {
            ...previous,
            [vote.message_id]: payload.eventType === 'DELETE' ? current : [...current, vote],
          };
        });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, (payload) => {
        const profile = payload.new;
        if (!profile?.id) return;
        setProfiles((previous) => {
          const exists = previous.some((item) => item.id === profile.id);
          const next = exists
            ? previous.map((item) => item.id === profile.id ? { ...item, ...profile } : item)
            : [...previous, profile];
          return next.sort((left, right) => (left.username || '').localeCompare(right.username || ''));
        });
        setConversations((previous) => previous.map((conversation) => (
          conversation.peer?.id === profile.id
            ? { ...conversation, peer: { ...conversation.peer, ...profile } }
            : conversation
        )));
      })
      .subscribe((status, error) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('Unable to subscribe to chat changes:', error || status);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadChatData, user?.id]);

  useEffect(() => {
    if (!scheduledMessageHides.length) return undefined;
    const nextExpiry = Math.min(...scheduledMessageHides.map((row) => new Date(row.hide_at).getTime()));
    const timeout = window.setTimeout(() => {
      const now = Date.now();
      const expiredIds = scheduledMessageHides
        .filter((row) => new Date(row.hide_at).getTime() <= now)
        .map((row) => String(row.message_id));
      if (expiredIds.length) {
        setHiddenMessageIds((previous) => [...new Set([...previous, ...expiredIds])]);
        Promise.all(expiredIds.map(async (messageId) => {
          const { data, error } = await supabase.rpc('hide_message_for_me', {
            p_message_id: Number(messageId),
            p_hide_after_seconds: null,
          });
          if (error) throw error;
          if (data?.deleted) {
            setChatMessagesMap((previous) => Object.fromEntries(
              Object.entries(previous).map(([conversationId, rows]) => [
                conversationId,
                rows.filter((message) => String(message.id) !== messageId),
              ])
            ));
            setMessages((previous) => previous.filter((message) => String(message.id) !== messageId));
          }
        })).catch((error) => {
          console.error('Unable to finalize expired message hides:', error);
          setHideMessagesError('Disa fshirje të planifikuara nuk u përditësuan në server.');
        });
      }
      setScheduledMessageHides((previous) => previous.filter((row) => new Date(row.hide_at).getTime() > now));
    }, Math.max(0, nextExpiry - Date.now()) + 25);
    return () => window.clearTimeout(timeout);
  }, [scheduledMessageHides]);

  // Personal notes are listed alongside other conversations.
  const myNotesChat = useMemo(() => {
    const latestNote = notesMessages[notesMessages.length - 1];
    return {
      id: notesConversationId || 'notes_me',
      kind: 'notes',
      title: 'Shënimet e mia',
      snippet: getConversationSnippet(latestNote, 'Shënimet e mia personale...').split('\n')[0],
      time: latestNote ? formatConversationListTime(latestNote.created_at, language) : '',
      unread: 0,
    };
  }, [notesMessages, notesConversationId, language]);

  // Keep My Notes in the same list as direct and group conversations.
  const allChats = useMemo(() => {
    const regularChats = conversations.map((c) => ({
      ...c,
      unread: unreadMap[c.id] ?? 0,
      snippet: getConversationSnippet(
        chatMessagesMap[c.id]?.[chatMessagesMap[c.id].length - 1],
        c.snippet
      ),
      time: chatMessagesMap[c.id]?.length
        ? formatConversationListTime(chatMessagesMap[c.id][chatMessagesMap[c.id].length - 1].created_at, language)
        : c.time,
    }));
    return [myNotesChat, ...regularChats];
  }, [conversations, unreadMap, chatMessagesMap, myNotesChat, language]);

  const visibleChats = useMemo(() => allChats.filter((chat) => {
    if (chat.kind === 'notes') return chatFilter === 'all';
    if (hiddenChatIds.includes(chat.id) && chatFilter !== 'hidden') return false;
    if (chatFilter === 'unread' && !(chat.unread > 0)) return false;
    if (chatFilter === 'muted' && !mutedChatIds.includes(chat.id)) return false;
    if (chatFilter === 'hidden' && !hiddenChatIds.includes(chat.id)) return false;
    if (chatFilter !== 'hidden' && mutedChatIds.includes(chat.id) && chatFilter === 'all') return true;
    return true;
  }).sort((left, right) => (
    Number(pinnedChatIds.includes(right.id)) - Number(pinnedChatIds.includes(left.id))
  )), [allChats, chatFilter, hiddenChatIds, mutedChatIds, pinnedChatIds]);

  const togglePinnedChat = useCallback(async (chatId) => {
    if (!user?.id || pinningChatId || !chatId) return false;

    const isPinned = pinnedChatIds.includes(chatId);
    setConversationPinError('');
    setPinningChatId(chatId);
    try {
      const result = isPinned
        ? await supabase
          .from('conversation_pins')
          .delete()
          .eq('conversation_id', chatId)
          .eq('user_id', user.id)
        : await supabase
          .from('conversation_pins')
          .insert({ conversation_id: chatId, user_id: user.id });

      if (result.error) throw result.error;
      setPinnedChatIds((current) => (
        isPinned
          ? current.filter((id) => id !== chatId)
          : [chatId, ...current]
      ));
      return true;
    } catch (error) {
      console.error('Unable to update pinned conversation in Supabase:', error);
      setConversationPinError('Ndryshimi nuk u ruajt. Kontrollo përditësimin e Supabase dhe provo sërish.');
      return false;
    } finally {
      setPinningChatId(null);
    }
  }, [pinnedChatIds, pinningChatId, user?.id]);

  useEffect(() => {
    setMutedChatIds(readActiveMutedChatIds(user?.id));
    setMutedChatExpiries(readMutedChatExpiries(user?.id));
  }, [mutedChatsStorageKey, mutedChatExpiriesStorageKey, user?.id]);

  const saveMutedChatPreferences = useCallback((chatIds, expiries) => {
    try {
      localStorage.setItem(mutedChatsStorageKey, JSON.stringify(chatIds));
      localStorage.setItem(mutedChatExpiriesStorageKey, JSON.stringify(expiries));
      setMutedChatIds(chatIds);
      setMutedChatExpiries(expiries);
      return true;
    } catch (error) {
      console.error('Unable to save muted chat settings:', error);
      return false;
    }
  }, [mutedChatsStorageKey, mutedChatExpiriesStorageKey]);

  const muteChat = useCallback((chatId, durationSeconds) => {
    const nextIds = [...new Set([...mutedChatIds, chatId])];
    const nextExpiries = { ...mutedChatExpiries };
    nextExpiries[chatId] = durationSeconds === null
      ? null
      : new Date(Date.now() + durationSeconds * 1000).toISOString();
    return saveMutedChatPreferences(nextIds, nextExpiries);
  }, [mutedChatIds, mutedChatExpiries, saveMutedChatPreferences]);

  const unmuteChat = useCallback((chatId) => {
    const nextExpiries = { ...mutedChatExpiries };
    delete nextExpiries[chatId];
    return saveMutedChatPreferences(
      mutedChatIds.filter((id) => id !== chatId),
      nextExpiries
    );
  }, [mutedChatIds, mutedChatExpiries, saveMutedChatPreferences]);

  useEffect(() => {
    const now = Date.now();
    const expiredChatIds = mutedChatIds.filter((chatId) => (
      mutedChatExpiries[chatId] &&
      new Date(mutedChatExpiries[chatId]).getTime() <= now
    ));
    if (expiredChatIds.length) {
      const expired = new Set(expiredChatIds);
      const nextExpiries = { ...mutedChatExpiries };
      expiredChatIds.forEach((chatId) => delete nextExpiries[chatId]);
      saveMutedChatPreferences(
        mutedChatIds.filter((chatId) => !expired.has(chatId)),
        nextExpiries
      );
      return undefined;
    }

    const nextExpiry = Math.min(...mutedChatIds
      .map((chatId) => mutedChatExpiries[chatId])
      .filter(Boolean)
      .map((expiry) => new Date(expiry).getTime()));
    if (!Number.isFinite(nextExpiry)) return undefined;

    const timeout = window.setTimeout(() => {
      const currentTime = Date.now();
      const expired = new Set(mutedChatIds.filter((chatId) => (
        mutedChatExpiries[chatId] &&
        new Date(mutedChatExpiries[chatId]).getTime() <= currentTime
      )));
      const nextExpiries = { ...mutedChatExpiries };
      expired.forEach((chatId) => delete nextExpiries[chatId]);
      saveMutedChatPreferences(
        mutedChatIds.filter((chatId) => !expired.has(chatId)),
        nextExpiries
      );
    }, Math.max(0, nextExpiry - now) + 25);
    return () => window.clearTimeout(timeout);
  }, [mutedChatIds, mutedChatExpiries, saveMutedChatPreferences]);

  const toggleHiddenChat = useCallback((chatId) => {
    setHiddenChatIds((previous) => {
      const next = previous.includes(chatId)
        ? previous.filter((id) => id !== chatId)
        : [...previous, chatId];
      localStorage.setItem(`hidden_chats_${user?.id || 'default'}`, JSON.stringify(next));
      return next;
    });
  }, [user?.id]);

  useEffect(() => {
    if (selected && selected.kind !== 'notes') {
      const refreshed = allChats.find((chat) => chat.id === selected.id);
      if (refreshed) setSelected(refreshed);
    }
  }, [allChats, selected]);

  useEffect(() => {
    setEditingMessage(null);
    setReplyingTo(null);
    setMentionQuery(null);
    setIsMultiSelectMode(false);
    setSelectedMessageIds([]);
    pendingOlderMessagesScrollRef.current = null;
    atMessagesBottomRef.current = true;
    setIsAtMessagesBottom(true);
    setMessageWindow({ conversationId: selected?.id || null, count: 20 });
  }, [selected?.id]);

  useEffect(() => {
    setNewMessage(selected?.id ? chatDrafts[selected.id] || '' : '');
  }, [selected?.id, chatDrafts]);

  // When selected chat changes, load its messages & clear unread
  useEffect(() => {
    if (!selected) {
      setMessages([]);
      return;
    }

    window.__CURRENT_ACTIVE_CHAT_ID = selected?.id || null;

    if (user?.id && selected?.id && selected.kind !== 'notes') {
      supabase
        .from('messages')
        .select('id')
        .eq('conversation_id', selected.id)
        .neq('sender_id', user.id)
        .then(({ data: msgs }) => {
          if (msgs && msgs.length) {
            const rows = msgs.map((m) => ({
              message_id: m.id,
              user_id: user.id,
              read_at: new Date().toISOString(),
            }));
            supabase.from('message_reads').upsert(rows, { onConflict: 'message_id,user_id' });
          }
        });
    }

    if (selected.kind === 'notes') {
      setMessages(notesMessages);
    } else {
      const msgs = chatMessagesMap[selected.id] || [];
      const unreadIds = msgs
        .filter((message) => message.sender_id !== user?.id && !messageReadsMap[message.id]?.some((read) => read.user_id === user?.id))
        .map((message) => message.id);
      if (unreadIds.length && user?.id) {
        const readAt = new Date().toISOString();
        const rows = unreadIds.map((messageId) => ({
          message_id: messageId,
          user_id: user.id,
          read_at: readAt,
        }));
        supabase.from('message_reads').upsert(rows, { onConflict: 'message_id,user_id' })
          .then(({ error }) => {
            if (error) console.error('Unable to mark messages as read:', error);
          });
        setMessageReadsMap((previous) => ({
          ...previous,
          ...unreadIds.reduce((result, messageId) => {
            result[messageId] = [...(previous[messageId] || []), {
              message_id: messageId,
              user_id: user.id,
              read_at: readAt,
            }];
            return result;
          }, {}),
        }));
      }
      const updatedMsgs = msgs.map((m) => ({
        ...m,
        seen: m.sender_id === user?.id
          ? Boolean(messageReadsMap[m.id]?.some((read) => read.user_id !== user?.id))
          : false,
      }));
      setMessages(updatedMsgs);

      if (unreadMap[selected.id] > 0) {
        setUnreadMap((prev) => ({ ...prev, [selected.id]: 0 }));
      }
    }
  }, [selected, notesMessages, chatMessagesMap, messageReadsMap, unreadMap, user?.id]);

  useLayoutEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    const pendingScroll = pendingOlderMessagesScrollRef.current;
    if (pendingScroll) {
      if (pendingScroll.conversationId === selected?.id && messagesContainer) {
        messagesContainer.scrollTop = pendingScroll.scrollTop
          + messagesContainer.scrollHeight
          - pendingScroll.scrollHeight;
        atMessagesBottomRef.current = false;
      }
      pendingOlderMessagesScrollRef.current = null;
      return;
    }

    const latestMessage = messages[messages.length - 1];
    const previousLatest = previousLatestMessageRef.current;
    const conversationChanged = previousLatest.conversationId !== selected?.id;
    const newMessageAdded = previousLatest.messageId !== latestMessage?.id;
    if (
      messagesContainer &&
      (conversationChanged || (
        newMessageAdded &&
        (atMessagesBottomRef.current || latestMessage?.sender_id === user?.id)
      ))
    ) {
      messagesContainer.scrollTo({ top: messagesContainer.scrollHeight, behavior: 'auto' });
      atMessagesBottomRef.current = true;
      setIsAtMessagesBottom(true);
    }
    previousLatestMessageRef.current = {
      conversationId: selected?.id,
      messageId: latestMessage?.id || null,
    };
  }, [messages, selected?.id, user?.id, messageWindow]);

  // Copy message text
  const handleCopyText = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Toggle Reaction
  const handleToggleReaction = async (messageId, emoji) => {
    const currentReactions = Array.isArray(reactionsMap[messageId]) ? reactionsMap[messageId] : [];
    const existing = currentReactions.find((reaction) => reaction.user_id === user?.id);
    if (user?.id && messageId && !String(messageId).startsWith('note_')) {
      const result = existing?.emoji === emoji
        ? await supabase.from('message_reactions').delete().eq('message_id', messageId).eq('user_id', user.id)
        : await supabase.from('message_reactions').upsert(
          { message_id: messageId, user_id: user.id, emoji },
          { onConflict: 'message_id,user_id' }
        );
      if (result.error) {
        console.error('Unable to save message reaction:', result.error);
        return;
      }
    }
    setReactionsMap((previous) => {
      const current = Array.isArray(previous[messageId]) ? previous[messageId] : [];
      const next = current.filter((reaction) => reaction.user_id !== user?.id);
      if (existing?.emoji !== emoji) {
        next.push({ message_id: messageId, user_id: user?.id, emoji });
      }
      return { ...previous, [messageId]: next };
    });
  };

  const handleToggleMessagePin = async (message) => {
    if (!message?.id || !Number.isFinite(Number(message.id)) || selected?.kind === 'notes') return;
    const pinned = Boolean(message.pinned_at);
    setMessagePinError('');
    const { data: nextPinnedAt, error } = await supabase.rpc('set_message_pinned', {
      p_message_id: Number(message.id),
      p_is_pinned: !pinned,
    });
    if (error) {
      console.error('Unable to pin message:', error);
      setMessagePinError('Fiksimi i mesazhit nuk u ruajt. Provo përsëri; nëse problemi vazhdon, përditëso skemën në Supabase.');
      return;
    }
    setChatMessagesMap((previous) => ({
      ...previous,
      [message.conversation_id]: (previous[message.conversation_id] || []).map((item) =>
        item.id === message.id ? { ...item, pinned_at: nextPinnedAt } : item
      ),
    }));
    setMessages((previous) => previous.map((item) => (
      item.id === message.id ? { ...item, pinned_at: nextPinnedAt } : item
    )));
    setDismissedPinnedBannerChatId(null);
  };

  const handleEditMessage = async (e) => {
    e?.preventDefault();
    if (!editingMessage || !newMessage.trim() || !user?.id) return;

    const content = newMessage.trim();
    const editedAt = new Date().toISOString();
    const { data, error } = await supabase
      .from('messages')
      .update({ content, edited_at: editedAt })
      .eq('id', editingMessage.id)
      .eq('sender_id', user.id)
      .select('id, conversation_id, content, edited_at')
      .single();
    if (error) {
      console.error('Unable to edit chat message:', error);
      return;
    }

    const updateMessage = (message) => message.id === data.id
      ? { ...message, content: data.content, edited_at: data.edited_at }
      : message;
    if (editingMessage.conversation_id === notesConversationId) {
      const updatedNotes = notesMessages.map(updateMessage);
      saveNotesMessages(updatedNotes);
      setMessages(updatedNotes);
    } else {
      const updatedMessages = (chatMessagesMap[editingMessage.conversation_id] || []).map(updateMessage);
      saveChatMessagesMap({ ...chatMessagesMap, [editingMessage.conversation_id]: updatedMessages });
      if (selected?.id === editingMessage.conversation_id) setMessages(updatedMessages);
    }
    setEditingMessage(null);
    setCurrentDraft('');
  };

  // Send new message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !selected || !user?.id) return;
    sendTyping(selected.id, false);
    if (editingMessage) {
      await handleEditMessage(e);
      return;
    }

    const messageText = newMessage.trim();
    const replyToId = Number.isFinite(Number(replyingTo?.id)) ? replyingTo.id : null;
    setCurrentDraft('');

    if (selected.kind === 'notes') {
      const { data, error } = await supabase.from('messages').insert({
        conversation_id: notesConversationId,
        sender_id: user.id,
        sender_name: getDisplayName(user),
        content: messageText,
        reply_to_id: replyToId,
      }).select('id, conversation_id, sender_id, sender_name, content, timestamp, reply_to_id').single();
      if (error) {
        console.error('Unable to save note:', error);
        setCurrentDraft(messageText);
        return;
      }
      const newNote = {
        ...data,
        created_at: data.timestamp,
        senderName: data.sender_name,
        seen: true,
        replyTo: replyingTo || null,
      };
      const updated = [...notesMessages, newNote];
      saveNotesMessages(updated);
      setMessages(updated);
      setReplyingTo(null);
      return;
    }

    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: selected.id,
        sender_id: user.id,
        sender_name: getDisplayName(user),
        content: messageText,
        reply_to_id: replyToId,
      })
      .select('id, conversation_id, sender_id, sender_name, content, timestamp, reply_to_id')
      .single();

    if (error) {
      console.error('Unable to send chat message:', error);
      setCurrentDraft(messageText);
      return;
    }

    const newMsg = {
      ...data,
      created_at: data.timestamp,
      senderName: data.sender_name,
      seen: false,
      replyTo: replyingTo || null,
    };
    const updated = [...(chatMessagesMap[selected.id] || []), newMsg];
    saveChatMessagesMap({ ...chatMessagesMap, [selected.id]: updated });
    setMessages(updated);
    setReplyingTo(null);
  };

  const handleCreatePoll = async ({ question, options }) => {
    if (!selected || selected.kind === 'notes' || !user?.id) return false;
    setIsCreatingPoll(true);
    setPollActionError('');
    try {
      const content = JSON.stringify({ question, options });
      const { data, error } = await supabase.from('messages').insert({
        conversation_id: selected.id,
        sender_id: user.id,
        sender_name: getDisplayName(user),
        content,
        message_type: 'poll',
      }).select('id, conversation_id, sender_id, sender_name, content, timestamp, message_type').single();
      if (error) throw error;
      const pollMessage = {
        ...data,
        created_at: data.timestamp,
        senderName: data.sender_name,
        seen: false,
        reactions: [],
      };
      setChatMessagesMap((previous) => ({
        ...previous,
        [selected.id]: [...(previous[selected.id] || []), pollMessage],
      }));
      setMessages((previous) => [...previous, pollMessage]);
      return true;
    } catch (error) {
      console.error('Unable to create chat poll:', error);
      setPollActionError('Anketa nuk u krijua. Kontrolloni nëse skema Supabase është përditësuar.');
      return false;
    } finally {
      setIsCreatingPoll(false);
    }
  };

  const handleVoteOnPoll = async (message, optionIndex) => {
    if (!message?.id || !user?.id || votingOnPollId === message.id) return;
    let poll;
    try {
      poll = JSON.parse(message.content);
    } catch {
      setPollActionError('Anketa ka format të pavlefshëm.');
      return;
    }
    if (!Array.isArray(poll.options) || !Number.isInteger(optionIndex) || optionIndex < 0 || optionIndex >= poll.options.length) {
      setPollActionError('Përgjigjja e zgjedhur nuk është e vlefshme.');
      return;
    }

    setVotingOnPollId(message.id);
    setPollActionError('');
    try {
      const vote = { message_id: message.id, user_id: user.id, option_index: optionIndex };
      const { error } = await supabase.from('message_poll_votes').upsert(vote, {
        onConflict: 'message_id,user_id',
      });
      if (error) throw error;
      setPollVotesMap((previous) => ({
        ...previous,
        [message.id]: [
          ...(previous[message.id] || []).filter((item) => item.user_id !== user.id),
          vote,
        ],
      }));
    } catch (error) {
      console.error('Unable to save poll vote:', error);
      setPollActionError('Vota nuk u ruajt. Provoni përsëri.');
    } finally {
      setVotingOnPollId(null);
    }
  };

  // Forward message handler
  const handleForwardMessage = async (targetChatId, msgToForward) => {
    const isNotesTarget = targetChatId === notesConversationId || targetChatId === 'notes_me';
    const conversationId = isNotesTarget ? notesConversationId : targetChatId;
    if (!user?.id || !conversationId || !msgToForward) return false;

    const { data, error } = await supabase.from('messages').insert({
      conversation_id: conversationId,
      sender_id: user.id,
      sender_name: getDisplayName(user),
      content: msgToForward.content || '',
      message_type: msgToForward.message_type || 'text',
      attachment_url: msgToForward.attachment_url || null,
      attachment_name: msgToForward.attachment_name || null,
      attachment_type: msgToForward.attachment_type || null,
      attachment_size: msgToForward.attachment_size || null,
      forwarded: true,
    }).select('id, conversation_id, sender_id, sender_name, content, timestamp, message_type, attachment_url, attachment_name, attachment_type, attachment_size, forwarded').single();
    if (error) {
      console.error('Unable to forward chat message:', error);
      return false;
    }

    const forwardedMessage = {
      ...data,
      created_at: data.timestamp,
      senderName: data.sender_name,
      seen: isNotesTarget,
    };
    if (isNotesTarget) {
      const updated = [...notesMessages, forwardedMessage];
      saveNotesMessages(updated);
      if (selected?.kind === 'notes') setMessages(updated);
    } else {
      const updated = [...(chatMessagesMap[conversationId] || []), forwardedMessage];
      saveChatMessagesMap({ ...chatMessagesMap, [conversationId]: updated });
      if (selected?.id === conversationId) setMessages(updated);
    }
    return true;
  };

  const handleDeleteMessagesForEveryone = async (messagesToDelete) => {
    if (!messagesToDelete.length || !user?.id || selected?.kind === 'notes') return false;
    if (messagesToDelete.some((message) => message.sender_id !== user.id)) return false;

    const ids = messagesToDelete.map((message) => Number(message.id));
    if (ids.some((id) => !Number.isSafeInteger(id) || id <= 0)) return false;
    const conversationId = messagesToDelete[0].conversation_id || selected?.id;
    if (!conversationId || messagesToDelete.some(
      (message) => (message.conversation_id || selected?.id) !== conversationId
    )) return false;

    const { data: deletedRows, error } = await supabase.from('messages')
      .delete()
      .in('id', ids)
      .eq('conversation_id', conversationId)
      .eq('sender_id', user.id)
      .select('id');
    if (error) {
      console.error('Unable to delete chat messages for everyone:', error);
      return false;
    }
    const deletedIds = new Set((deletedRows || []).map((message) => String(message.id)));
    if (deletedIds.size !== ids.length || ids.some((id) => !deletedIds.has(String(id)))) {
      console.error('Unable to delete chat messages for everyone: Supabase did not delete every requested message.');
      return false;
    }
    const idsToDelete = new Set(ids.map(String));
    setChatMessagesMap((previous) => ({
      ...previous,
      [conversationId]: (previous[conversationId] || [])
        .filter((message) => !idsToDelete.has(String(message.id))),
    }));
    setMessages((previous) => previous.filter((message) => !idsToDelete.has(String(message.id))));
    if (isMultiSelectMode) {
      setSelectedMessageIds([]);
      setIsMultiSelectMode(false);
    }
    return true;
  };

  const handleDeleteMessage = async (message) => {
    if (!message) return false;
    return handleDeleteMessagesForEveryone([message]);
  };

  const hideMessagesForMe = async (messageIds, hideAfterSeconds = null) => {
    if (!user?.id) return false;
    const ids = [...new Set(messageIds.map(Number).filter((id) => Number.isSafeInteger(id) && id > 0))];
    if (ids.length !== messageIds.length) {
      setHideMessagesError('Nuk mund të fshihen disa mesazhe të zgjedhura.');
      return false;
    }

    setHideMessagesError('');
    try {
      const results = await Promise.allSettled(ids.map(async (messageId) => {
        const { data, error } = await supabase.rpc('hide_message_for_me', {
          p_message_id: messageId,
          p_hide_after_seconds: hideAfterSeconds,
        });
        if (error) throw error;
        if (!data || typeof data.deleted !== 'boolean') {
          throw new Error('The message hide request returned an invalid response.');
        }
        if (!data.deleted && data.hide_at !== null) {
          const hideAt = new Date(data.hide_at).getTime();
          if (!Number.isFinite(hideAt)) {
            throw new Error('The message hide request returned an invalid expiry time.');
          }
        }
        return { messageId, result: data };
      }));
      const now = Date.now();
      const immediatelyHidden = [];
      const scheduled = [];
      let hasFailures = false;
      results.forEach((requestResult) => {
        if (requestResult.status === 'rejected') {
          hasFailures = true;
          console.error('Unable to hide a chat message for this user:', requestResult.reason);
          return;
        }
        const { messageId, result } = requestResult.value;
        if (result?.deleted) {
          const id = String(messageId);
          setChatMessagesMap((previous) => Object.fromEntries(
            Object.entries(previous).map(([conversationId, rows]) => [
              conversationId,
              rows.filter((message) => String(message.id) !== id),
            ])
          ));
          setMessages((previous) => previous.filter((message) => String(message.id) !== id));
          return;
        }
        if (!result?.hide_at || new Date(result.hide_at).getTime() <= now) {
          immediatelyHidden.push(String(messageId));
        } else {
          scheduled.push({ message_id: messageId, hide_at: result.hide_at });
        }
      });
      if (immediatelyHidden.length) {
        setHiddenMessageIds((previous) => [...new Set([...previous, ...immediatelyHidden])]);
      }
      if (scheduled.length) {
        setScheduledMessageHides((previous) => [
          ...previous.filter((row) => !scheduled.some((item) => item.message_id === row.message_id)),
          ...scheduled,
        ]);
      }
      if (hasFailures) {
      setHideMessagesError('Disa mesazhe nuk u fshehën. Provoni përsëri.');
      }
      return !hasFailures;
    } catch (error) {
      console.error('Unable to hide chat messages for this user:', error);
      setHideMessagesError('Mesazhet nuk u fshinë. Provoni përsëri.');
      return false;
    }
  };

  const toggleSelectedMessage = (messageId) => {
    setSelectedMessageIds((current) => (
      current.includes(messageId)
        ? current.filter((id) => id !== messageId)
        : [...current, messageId]
    ));
  };

  const handleHideDeleteTargetsForMe = async (hideAfterSeconds = null) => {
    if (!targetDeleteMessages.length) return false;
    const hidden = selected?.kind === 'notes'
      ? hideNotesForMe(targetDeleteMessages, hideAfterSeconds)
      : await hideMessagesForMe(
        targetDeleteMessages.map((message) => message.id),
        hideAfterSeconds
      );
    if (!hidden) return false;
    if (isMultiSelectMode) {
      setSelectedMessageIds([]);
      setIsMultiSelectMode(false);
    }
    return true;
  };

  // Clear/Trash current conversation
  const handleClearConversation = async (period) => {
    if (!selected) return false;

    if (selected.kind === 'notes') {
      return hideNotesForMe(notesMessages, period);
    }

    if (!user?.id) return false;
    const messagesToHide = messages.filter(
      (message) => !hiddenMessageIds.includes(String(message.id))
    );
    if (!messagesToHide.length) return true;
    return hideMessagesForMe(
      messagesToHide.map((message) => message.id),
      period
    );
  };

  // Voice Recording handlers
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      let recorder;
      try {
        recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      } catch (e) {
        recorder = new MediaRecorder(stream);
      }

      mediaRecorderRef.current = recorder;
      setRecordingStream(stream);

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        setRecordingStream(null);

        const audioBlob = new Blob(audioChunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        if (audioBlob.size < 500) return;

        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Audio = reader.result;
          const voiceMsg = {
            id: `msg_audio_${Date.now()}`,
            conversation_id: selected.id,
            sender_id: user?.id || 'self',
            senderName: getDisplayName(user),
            content: JSON.stringify({
              type: 'audio',
              audioUrl: base64Audio,
              duration: recordingDuration,
            }),
            created_at: new Date().toISOString(),
            seen: selected.kind === 'notes',
          };

          if (selected.kind === 'notes') {
            const updated = [...notesMessages, voiceMsg];
            saveNotesMessages(updated);
            setMessages(updated);
          } else {
            const path = `${user.id}/${selected.id}/voice-${Date.now()}.webm`;
            supabase.storage.from('chat-attachments').upload(path, audioBlob, {
              upsert: false,
              contentType: 'audio/webm',
            }).then(({ error: uploadError }) => {
              if (uploadError) throw uploadError;
              return supabase.storage.from('chat-attachments').getPublicUrl(path);
            }).then(({ data: urlData }) => supabase.from('messages').insert({
              conversation_id: selected.id,
              sender_id: user.id,
              sender_name: getDisplayName(user),
              content: JSON.stringify({ type: 'audio', audioUrl: urlData.publicUrl, duration: recordingDuration }),
              message_type: 'audio',
              attachment_url: urlData.publicUrl,
              attachment_name: 'voice-message.webm',
              attachment_type: 'audio/webm',
              attachment_size: audioBlob.size,
            }).select('id, conversation_id, sender_id, sender_name, content, timestamp, message_type, attachment_url, attachment_name, attachment_type, attachment_size').single())
              .then(({ data, error }) => {
                if (error) {
                  console.error('Unable to send voice message:', error);
                  return;
                }
                const savedMessage = {
                  ...data,
                  created_at: data.timestamp,
                  senderName: data.sender_name,
                  seen: false,
                };
                const currentMsgs = chatMessagesMap[selected.id] || [];
                const updated = [...currentMsgs, savedMessage];
                saveChatMessagesMap({ ...chatMessagesMap, [selected.id]: updated });
                setMessages(updated);
              }).catch((error) => console.error('Unable to upload voice message:', error));
          }
        };
        reader.readAsDataURL(audioBlob);
      };

      recorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((p) => p + 1);
      }, 1000);
    } catch (err) {
      console.warn('Microphone error:', err);
      alert('Ju lutem lejoni qasjen në mikrofon për të regjistruar zë.');
    }
  };

  const cancelRecording = () => {
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingStream) recordingStream.getTracks().forEach((t) => t.stop());
    setIsRecording(false);
  };

  const stopAndSendRecording = () => {
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  // Filtered chats by search
  const filteredChats = useMemo(() => {
    if (!search.trim()) return visibleChats;
    return visibleChats.filter((c) =>
      c.title.toLowerCase().includes(search.toLowerCase())
    );
  }, [visibleChats, search]);

  const chatListEntries = useMemo(() => {
    const pinnedChats = filteredChats
      .filter((chat) => pinnedChatIds.includes(chat.id))
      .sort((left, right) => Number(left.kind === 'notes') - Number(right.kind === 'notes'));
    const regularChats = filteredChats.filter((chat) => (
      !pinnedChatIds.includes(chat.id)
    ));

    return [
      ...(pinnedChats.length
        ? [{ type: 'section', id: 'pinned-section', label: 'TË FIKSUARA' }]
        : []),
      ...pinnedChats.map((chat) => ({ type: 'chat', id: chat.id, chat })),
      ...(pinnedChats.length && regularChats.length
        ? [{ type: 'section', id: 'chats-section', label: 'TË TJERAT' }]
        : []),
      ...regularChats.map((chat) => ({ type: 'chat', id: chat.id, chat })),
    ];
  }, [filteredChats, pinnedChatIds]);

  useEffect(() => {
    const requestedConversationId = location.state?.chatConversationId;
    if (!requestedConversationId) return;
    const conversation = allChats.find((chat) => chat.id === requestedConversationId);
    if (!conversation) return;
    setSelected(conversation);
    setMobileShowList(false);
    navigate(location.pathname, { replace: true, state: null });
  }, [allChats, location.pathname, location.state, navigate]);

  const mentionCandidates = useMemo(() => {
    if (!selected || selected.kind === 'notes') return [];
    const memberIds = selected.kind === 'group'
      ? selected.memberIds || []
      : [selected.peer?.id].filter(Boolean);
    return profiles.filter((profile) => profile.id !== user?.id && memberIds.includes(profile.id));
  }, [profiles, selected, user?.id]);

  const filteredMentionCandidates = useMemo(() => {
    if (mentionQuery === null) return [];
    const query = mentionQuery.toLocaleLowerCase();
    return mentionCandidates.filter((profile) => {
      const name = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
      return name.toLocaleLowerCase().includes(query);
    }).slice(0, 6);
  }, [mentionCandidates, mentionQuery]);

  const handleSelectEmoji = (emoji) => {
    const input = composerInputRef.current;
    const currentMessage = input?.value ?? newMessage;
    const start = input?.selectionStart ?? currentMessage.length;
    const end = input?.selectionEnd ?? start;
    const nextMessage = `${currentMessage.slice(0, start)}${emoji}${currentMessage.slice(end)}`;
    const nextCursor = start + emoji.length;

    setCurrentDraft(nextMessage);
    if (input) input.value = nextMessage;
    setShowEmojiPicker(false);
    setMentionQuery(null);
    window.requestAnimationFrame(() => {
      if (!input) return;
      input.focus();
      input.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const handleSelectMention = (profile) => {
    const input = composerInputRef.current;
    if (!input) return;
    const cursor = input.selectionStart ?? newMessage.length;
    const beforeCursor = newMessage.slice(0, cursor);
    const mentionMatch = beforeCursor.match(/(?:^|\s)@([\w.-]*)$/);
    if (!mentionMatch) return;
    const mentionStart = cursor - mentionMatch[0].length + (mentionMatch[0].startsWith(' ') ? 1 : 0);
    const name = getDisplayName(profile, '').replace(/\s+/g, '');
    const nextValue = `${newMessage.slice(0, mentionStart)}@${name} ${newMessage.slice(cursor)}`;
    const nextCursor = mentionStart + name.length + 2;
    setCurrentDraft(nextValue);
    setMentionQuery(null);
    window.requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const chronologicallySortedMessages = useMemo(() => (
    [...messages].sort((left, right) => (
      new Date(left.created_at).getTime() - new Date(right.created_at).getTime()
    ))
  ), [messages]);
  const matchingMessages = useMemo(() => {
    const visibleMessages = chronologicallySortedMessages.filter(
      (message) => !hiddenMessageIds.includes(String(message.id))
    );
    if (!inChatSearchQuery.trim()) return visibleMessages;
    return visibleMessages.filter((message) =>
      message.content?.toLowerCase().includes(inChatSearchQuery.toLowerCase())
    );
  }, [chronologicallySortedMessages, inChatSearchQuery, hiddenMessageIds]);
  const visibleMessageCount = messageWindow.conversationId === selected?.id
    ? messageWindow.count
    : 20;
  const displayedMessages = matchingMessages.slice(
    Math.max(0, matchingMessages.length - visibleMessageCount)
  );
  const hasOlderMessages = displayedMessages.length < matchingMessages.length;

  const loadOlderMessages = () => {
    if (pendingOlderMessagesScrollRef.current || !selected?.id || !hasOlderMessages) return;
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;
    pendingOlderMessagesScrollRef.current = {
      conversationId: selected.id,
      scrollHeight: messagesContainer.scrollHeight,
      scrollTop: messagesContainer.scrollTop,
    };
    setMessageWindow({
      conversationId: selected.id,
      count: Math.min(visibleMessageCount + 20, matchingMessages.length),
    });
  };

  const scrollMessagesToBottom = () => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;
    messagesContainer.scrollTo({ top: messagesContainer.scrollHeight, behavior: 'smooth' });
    atMessagesBottomRef.current = true;
    setIsAtMessagesBottom(true);
  };
  const latestOutgoingMessageId = useMemo(
    () => [...messages]
      .reverse()
      .find((message) => (
        message.sender_id === user?.id &&
        !hiddenMessageIds.includes(String(message.id))
      ))?.id,
    [messages, user?.id, hiddenMessageIds]
  );

  const sharedAttachments = useMemo(
    () => messages.filter((message) => (
      !hiddenMessageIds.includes(String(message.id)) &&
      message.attachment_url &&
      (mediaGalleryFilter === 'all' ||
        (mediaGalleryFilter === 'images' && message.attachment_type?.startsWith('image/')) ||
        (mediaGalleryFilter === 'files' && !message.attachment_type?.startsWith('image/')))
    )),
    [messages, mediaGalleryFilter, hiddenMessageIds]
  );

  const pinnedMessages = useMemo(
    () => messages
      .filter((message) => (
        message.pinned_at && !hiddenMessageIds.includes(String(message.id))
      ))
      .sort((left, right) => new Date(left.pinned_at) - new Date(right.pinned_at)),
    [messages, hiddenMessageIds]
  );
  const activePinnedMessage = pinnedMessages.length
    ? pinnedMessages[Math.min(pinnedMessageIndex, pinnedMessages.length - 1)]
    : null;
  const activePinnedSenderName = activePinnedMessage
    ? activePinnedMessage.sender_id === user?.id
      ? getDisplayName(user)
      : getDisplayName(profiles.find((profile) => profile.id === activePinnedMessage.sender_id), 'Student')
    : '';

  const goToPinnedMessage = (index) => {
    if (!pinnedMessages.length) return;
    const nextIndex = (index + pinnedMessages.length) % pinnedMessages.length;
    setPinnedMessageIndex(nextIndex);
    document.getElementById(`chat-message-${pinnedMessages[nextIndex].id}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  useEffect(() => {
    setPinnedMessageIndex(0);
    setMessagePinError('');
  }, [selected?.id]);

  // Render message content
  const renderMessageBody = (content, isMe, audioAttachmentUrl) => {
    let audioMessage = null;
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        if (parsed?.type === 'audio') audioMessage = parsed;
      } catch (e) {}
    }

    if (audioMessage || audioAttachmentUrl) {
      return (
        <AudioPlayer
          audioUrl={audioMessage?.audioUrl || audioAttachmentUrl}
          duration={audioMessage?.duration}
          isMe={isMe}
        />
      );
    }

    // 2. Multi-line Text with Twemoji / Apple styling
    return (
      <div className="whitespace-pre-wrap break-words leading-relaxed text-[13.5px]">
        <TwemojiText text={content} size={18} />
      </div>
    );
  };

  return (
    <DashboardLayout noPadding>
      <div className="grid h-full min-h-0 w-full min-w-0 grid-cols-1 gap-3 overflow-hidden bg-white p-3 font-sans md:grid-cols-[330px_minmax(0,1fr)] md:p-5">

        {/* =========================================================
            LEFT COLUMN: ENTRIKS-HR CHAT SIDEBAR
           ========================================================= */}
        <aside
          className={`flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-sm transition-all md:w-[330px] ${
            !mobileShowList ? 'hidden md:flex' : 'flex'
          }`}
        >
          <div aria-hidden="true" className="flex h-1 shrink-0">
            <span className="flex-1 bg-zinc-950" />
            <span className="flex-1 bg-[#DD0000]" />
            <span className="flex-1 bg-[#FFCE00]" />
          </div>
          {/* Header */}
          <div className="bg-card p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold tracking-tight text-foreground">Chat</h2>
              <div className="relative flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setShowChatSettings((previous) => !previous)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-accent hover:text-accent-foreground"
                  title="Chat settings"
                >
                  <Moon size={17} />
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewChatModal(true)}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:bg-accent hover:text-accent-foreground"
                  title="Bisedë e Re"
                >
                  <Plus size={16} />
                </button>
                {showChatSettings && (
                  <div className="absolute right-0 top-11 z-40 w-52 rounded-2xl border border-border bg-card p-2 shadow-xl">
                    <button
                      type="button"
                      onClick={() => setSoundEnabled((previous) => !previous)}
                      className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-semibold text-foreground hover:bg-accent"
                    >
                      <span className="flex items-center gap-2"><Moon size={14} />{t(soundEnabled ? 'Tingujt aktivë' : 'Tingujt joaktivë')}</span>
                      <span className={`h-2 w-2 rounded-full ${soundEnabled ? 'bg-emerald-500' : 'bg-muted-foreground'}`} />
                    </button>
                    <button
                      type="button"
                      onClick={toggleChatNotifications}
                      className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-semibold text-foreground hover:bg-accent"
                    >
                      <span className="flex items-center gap-2"><Bell size={14} />{t(notificationsEnabled ? 'Njoftimet aktive' : 'Njoftimet joaktive')}</span>
                      <span className={`h-2 w-2 rounded-full ${notificationsEnabled ? 'bg-emerald-500' : 'bg-muted-foreground'}`} />
                    </button>
                    {notificationPermissionError && (
                      <p role="alert" className="px-3 pb-1 text-[10px] text-red-600">{notificationPermissionError}</p>
                    )}
                    <p className="px-3 pb-1 pt-2 text-[10px] text-muted-foreground">{t('Cilësimet e chat-it')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-card px-4 pb-2 pt-3">
            <div className="relative">
              <Search className="absolute left-3.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('Kërko person ose bisedë')}
                className="h-9 w-full rounded-lg border border-input bg-muted/50 pl-9 pr-3 text-xs text-foreground placeholder:text-muted-foreground focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* Conversation Filter Header (Permanently Visible) */}
          <div className="flex items-center justify-between px-3.5 pb-2 pt-1 border-b border-zinc-100 dark:border-zinc-800">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground">
              {chatFilter === 'all'
                ? t('BISEDAT')
                : chatFilter === 'unread'
                ? t('TË PALEXUARA')
                : chatFilter === 'muted'
                ? t('TË HESHTURA')
                : t('TË FSHEHURA')}
            </span>
            <div className="relative" ref={chatFilterMenuRef}>
              <button
                type="button"
                onClick={() => setShowChatFilterMenu((open) => !open)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200/80 dark:border-white/10 bg-white/90 dark:bg-zinc-800/80 backdrop-blur-xs px-2.5 py-1 text-[11px] font-bold text-zinc-700 dark:text-zinc-200 shadow-2xs transition hover:border-[#FFCE00] hover:bg-amber-50/60 dark:hover:bg-white/10 active:scale-95"
                aria-haspopup="menu"
                aria-expanded={showChatFilterMenu}
              >
                <MessageCircle size={12} className="text-[#DD0000]" />
                <span>
                  {({
                    all: t('Të gjitha bisedat'),
                    unread: t('Të palexuara'),
                    muted: t('Të heshtura'),
                    hidden: t('Të fshehura'),
                  })[chatFilter]}
                </span>
                <ChevronLeft size={12} className={`-rotate-90 transition-transform ${showChatFilterMenu ? 'rotate-90' : ''}`} />
              </button>
              {showChatFilterMenu && (
                <div role="menu" className="absolute right-0 top-full z-40 mt-1.5 w-48 rounded-2xl border border-zinc-200/80 dark:border-white/15 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl p-1.5 shadow-xl animate-scaleIn">
                  {[
                    ['all', t('Të gjitha bisedat'), MessageCircle],
                    ['unread', t('Të palexuara'), CheckCheck],
                    ['muted', t('Të heshtura'), Bell],
                    ['hidden', t('Të fshehura'), Eye],
                  ].map(([filter, label, Icon]) => (
                    <button
                      key={filter}
                      type="button"
                      role="menuitemradio"
                      aria-checked={chatFilter === filter}
                      onClick={() => {
                        setChatFilter(filter);
                        setShowChatFilterMenu(false);
                      }}
                      className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-xs transition ${
                        chatFilter === filter
                          ? 'bg-amber-50/80 dark:bg-white/10 font-bold text-zinc-950 dark:text-zinc-50'
                          : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-white/5'
                      }`}
                    >
                      <Icon size={14} className={chatFilter === filter ? 'text-[#DD0000]' : 'text-zinc-400'} />
                      <span className="flex-1">{label}</span>
                      {filter === 'hidden' && hiddenChatIds.length > 0 && (
                        <span className="text-[10px] text-zinc-400">{hiddenChatIds.length}</span>
                      )}
                      {filter === 'muted' && mutedChatIds.length > 0 && (
                        <span className="text-[10px] text-zinc-400">{mutedChatIds.length}</span>
                      )}
                      {chatFilter === filter && <span className="h-1.5 w-1.5 rounded-full bg-[#FFCE00]" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col">
            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto px-2 space-y-1 custom-scrollbar">
              {directChatError && (
                <p role="alert" className="mx-1 mb-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{directChatError}</p>
              )}
              {conversationPinError && (
                <p role="alert" className="mx-1 mb-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{conversationPinError}</p>
              )}
              {chatListEntries.map((entry) => {
                if (entry.type === 'section') {
                  return (
                    <div key={entry.id} className="px-1 pb-1 pt-3">
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground">
                        {entry.label}
                      </span>
                    </div>
                  );
                }

                const c = entry.chat;
                const isSelected = selected?.id === c.id;
                const isOnline = c.peer?.isOnline || isUserOnline(c.peer?.id);
                const unreadCount = c.unread || 0;
                const isNotesChat = c.kind === 'notes';

                return (
                  <div key={c.id} className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setSelected(c);
                        setMobileShowList(false);
                      }}
                      className={`relative flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-left transition ${
                        isSelected
                          ? 'bg-accent shadow-sm'
                            : 'hover:bg-accent/50'
                      }`}
                    >
                      {/* Avatar with Online Status Dot (Matching Screenshot) */}
                      <div className="relative shrink-0">
                        {isNotesChat ? (
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                            <BookOpen size={17} />
                          </div>
                        ) : (
                          <>
                            <ProfileAvatar profile={c.peer} name={c.title} />
                            <span
                              className={`absolute -bottom-0.5 -right-0.5 z-10 h-3 w-3 rounded-full border-2 border-white shadow-sm ${
                                isOnline ? 'bg-emerald-500' : 'bg-zinc-500'
                              }`}
                              title={isOnline ? 'Online' : 'Offline'}
                            />
                          </>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <strong className="flex min-w-0 items-center gap-1 truncate text-xs font-bold text-foreground">
                            <span className="truncate">{c.title}</span>
                          </strong>
                          <span className="text-[10px] text-muted-foreground font-medium">{c.time}</span>
                        </div>

                        <div className="flex items-center justify-between mt-0.5">
                          {isConversationTyping(c.id) ? (
                            <TypingIndicator label="Duke shkruar..." />
                          ) : chatDrafts[c.id]?.trim() ? (
                            <span className="flex min-w-0 max-w-[160px] items-center gap-1 truncate text-[11px]">
                              <span className="shrink-0 font-semibold text-red-600">Draft:</span>
                              <TwemojiText
                                text={chatDrafts[c.id].trim()}
                                size={14}
                                disableLinks
                                className="block min-w-0 truncate text-muted-foreground"
                              />
                            </span>
                          ) : (
                            <TwemojiText
                              text={c.snippet}
                              size={14}
                              disableLinks
                              className="block max-w-[160px] truncate text-[11px] text-muted-foreground"
                            />
                          )}

                          <div className="flex items-center gap-1.5 shrink-0 ml-1">
                            {unreadCount > 0 && (
                              <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[#DD0000] px-1 text-[10px] font-bold text-white shadow-2xs">
                                {unreadCount}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setOpenChatMenuId((current) => current === c.id ? null : c.id);
                              }}
                              className="rounded-full p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                              aria-label={isNotesChat ? 'Opsionet e shënimeve të mia' : 'Opsionet e bisedës'}
                            >
                              <MoreHorizontal size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </button>
                    {openChatMenuId === c.id && (
                      <div
                        className="absolute right-3 top-12 z-30 w-48 rounded-xl border border-zinc-200 bg-white p-1.5 text-zinc-900 shadow-xl"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <button
                          type="button"
                          disabled={pinningChatId === c.id || (isNotesChat && !notesConversationId)}
                          onClick={async () => {
                            if (await togglePinnedChat(c.id)) setOpenChatMenuId(null);
                          }}
                          className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[11px] font-semibold hover:bg-zinc-100 disabled:opacity-50"
                        >
                          <Pin size={13} className={pinnedChatIds.includes(c.id) ? 'fill-amber-400 text-amber-700' : 'text-zinc-500'} />
                          {pinnedChatIds.includes(c.id) ? 'Hiqe nga të fiksuarat' : isNotesChat ? 'Fikso shënimet e mia' : 'Fikso bisedën'}
                        </button>
                        {!isNotesChat && <div aria-hidden="true" className="my-1 border-t border-zinc-200" />}
                        {!isNotesChat && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                if (mutedChatIds.includes(c.id)) {
                                  if (unmuteChat(c.id)) setOpenChatMenuId(null);
                                } else {
                                  setMuteTargetChat(c);
                                  setOpenChatMenuId(null);
                                }
                              }}
                              className="block w-full rounded-lg px-2 py-1.5 text-left text-[11px] font-semibold hover:bg-zinc-100"
                            >
                              {mutedChatIds.includes(c.id) ? 'Aktivizo njoftimet' : 'Hesht bisedën'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                toggleHiddenChat(c.id);
                                setOpenChatMenuId(null);
                              }}
                              className="block w-full rounded-lg px-2 py-1.5 text-left text-[11px] font-semibold hover:bg-zinc-100"
                            >
                              {hiddenChatIds.includes(c.id) ? 'Shfaq bisedën' : 'Fshih bisedën'}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredChats.length === 0 && (
                <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground animate-fadeIn">
                  <Bell size={26} className="text-muted-foreground/40 mb-2" />
                  <p className="text-xs font-semibold text-foreground">
                    {chatFilter === 'muted'
                      ? t('Nuk ka biseda të heshtura')
                      : chatFilter === 'unread'
                      ? t('Nuk ka biseda të palexuara')
                      : chatFilter === 'hidden'
                      ? t('Nuk ka biseda të fshehura')
                      : t('Nuk u gjet asnjë bisedë')}
                  </p>
                  {chatFilter !== 'all' && (
                    <button
                      type="button"
                      onClick={() => setChatFilter('all')}
                      className="mt-2 text-xs font-bold text-[#DD0000] hover:underline"
                    >
                      {t('Shfaq të gjitha bisedat')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* =========================================================
            RIGHT COLUMN: ACTIVE CHAT CONVERSATION
           ========================================================= */}
        <section
          className={`flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-sm ${
            mobileShowList ? 'hidden md:flex' : 'flex'
          }`}
        >
          <div aria-hidden="true" className="flex h-1 shrink-0">
            <span className="flex-1 bg-zinc-950" />
            <span className="flex-1 bg-[#DD0000]" />
            <span className="flex-1 bg-[#FFCE00]" />
          </div>
          {selected ? (
            <>
              {/* Active Conversation Header */}
              <header className="flex h-16 items-center justify-between border-b border-border bg-card px-4 sm:px-6 shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    type="button"
                    onClick={() => setMobileShowList(true)}
                    className="rounded-lg p-1 text-muted-foreground hover:bg-accent md:hidden"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  {/* Header Avatar */}
                  <div
                    onClick={() => {
                      if (selected.kind === 'group') setShowGroupInfoDialog(true);
                      else if (selected.kind === 'direct') setShowUserProfileDialog(true);
                    }}
                    className={`relative shrink-0 ${selected.kind !== 'notes' ? 'cursor-pointer' : ''}`}
                  >
                    {selected.kind === 'notes' ? (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary shadow-sm">
                        <BookOpen size={20} strokeWidth={2} />
                      </div>
                    ) : selected.kind === 'group' ? (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-700 text-white shadow-sm">
                        <Users size={20} strokeWidth={2} />
                      </div>
                    ) : (
                      <ProfileAvatar profile={selected.peer} name={selected.title} size="h-10 w-10" />
                    )}
                    {selected.kind === 'direct' && (
                      <span
                        className={`absolute -bottom-0.5 -right-0.5 z-10 h-3 w-3 rounded-full border-2 border-white shadow-sm ${
                          isUserOnline(selected.peer?.id) ? 'bg-emerald-500' : 'bg-zinc-500'
                        }`}
                      />
                    )}
                  </div>

                  <div className="min-w-0">
                    <h3
                      onClick={() => {
                        if (selected.kind === 'group') setShowGroupInfoDialog(true);
                        else if (selected.kind === 'direct') setShowUserProfileDialog(true);
                      }}
                      className={`${selected.kind !== 'notes' ? 'cursor-pointer hover:underline' : ''} truncate text-sm font-bold text-foreground`}
                    >
                      {selected.title}
                    </h3>
                    <p className="truncate text-xs text-muted-foreground">
                      {selected.kind === 'notes' ? (
                        'Shënimet e mia personale'
                      ) : selected.kind === 'group' ? (
                        `${selected.members?.length || selected.memberIds?.length || 0} anëtarë`
                      ) : isUserOnline(selected.peer?.id) ? (
                        <span className="text-emerald-600 font-medium">Aktiv tani</span>
                      ) : (
                        formatLastSeen(selected.peer?.last_seen_at)
                      )}
                    </p>
                  </div>
                </div>

                {/* Header Action Icons */}
                <div className="flex items-center gap-1 sm:gap-1.5 text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => setShowInChatSearch((p) => !p)}
                    className="rounded-xl p-2 hover:bg-accent hover:text-accent-foreground transition"
                    title="Kërko në bisedë"
                  >
                    <Search size={18} />
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowClearConversationDialog(true)}
                    className="rounded-xl p-2 text-red-500 hover:bg-red-50 hover:text-red-700 transition"
                    title="Fshi bisedën nga llogaria jote"
                    aria-label="Fshi bisedën nga llogaria jote"
                  >
                    <Trash2 size={18} />
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowMediaGallery(true)}
                    className="rounded-xl p-2 hover:bg-accent hover:text-accent-foreground transition"
                    title="Skedarët e ndarë"
                  >
                    <LayoutGrid size={18} />
                  </button>

                  {selected.kind !== 'notes' && (
                    <button
                      type="button"
                      onClick={handleStartCall}
                      className="rounded-xl p-2 hover:bg-accent hover:text-accent-foreground transition"
                      title="Nis thirrje me zë ose video (Google Meet style)"
                    >
                      <Phone size={18} />
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      if (selected.kind === 'group') setShowGroupInfoDialog(true);
                      else if (selected.kind === 'direct') setShowUserProfileDialog(true);
                    }}
                    className="rounded-xl p-2 hover:bg-accent hover:text-accent-foreground transition"
                    title="Informacion"
                  >
                    <Info size={18} />
                  </button>
                </div>
              </header>

              {/* In-chat search bar */}
              {showInChatSearch && (
                <div className="flex items-center gap-2 border-b border-border bg-muted/50 px-4 py-2">
                  <Search size={14} className="text-muted-foreground" />
                  <input
                    autoFocus
                    value={inChatSearchQuery}
                    onChange={(e) => setInChatSearchQuery(e.target.value)}
                    placeholder={t('Kërko në mesazhe...')}
                    className="w-full bg-transparent text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowInChatSearch(false);
                      setInChatSearchQuery('');
                    }}
                    className="rounded-lg p-1 text-muted-foreground hover:text-foreground"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {messagePinError && (
                <p role="alert" className="mx-4 mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                  {messagePinError}
                </p>
              )}
              {pinnedMessages.length > 0 && dismissedPinnedBannerChatId !== selected.id && (
                <div className="mx-3 mt-2 flex min-h-9 shrink-0 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1 shadow-sm sm:mx-4">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-[#DD0000]">
                    <Pin size={14} className="fill-[#FFCE00]" />
                  </span>
                  <button
                    type="button"
                    onClick={() => goToPinnedMessage(pinnedMessageIndex)}
                    className="min-w-0 flex-1 truncate text-left text-xs text-zinc-600"
                    title="Shko te mesazhi i fiksuar"
                  >
                    <span className="font-semibold text-[#DD0000]">
                      {t('Fiksuar')} {Math.min(pinnedMessageIndex + 1, pinnedMessages.length)}/{pinnedMessages.length}
                    </span>
                    <span>{` · ${activePinnedSenderName}: `}</span>
                    <span>
                      {t(getConversationSnippet(
                        activePinnedMessage,
                        activePinnedMessage?.attachment_name || 'Skedar'
                      ))}
                    </span>
                  </button>
                  {pinnedMessages.length > 1 && (
                    <div className="flex shrink-0 items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => goToPinnedMessage(pinnedMessageIndex - 1)}
                        className="rounded p-1 text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-950"
                        aria-label="Shko te mesazhi i fiksuar i mëparshëm"
                        title="Mesazhi i mëparshëm"
                      >
                        <ChevronLeft size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => goToPinnedMessage(pinnedMessageIndex + 1)}
                        className="rounded p-1 text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-950"
                        aria-label="Shko te mesazhi i fiksuar pasues"
                        title="Mesazhi tjetër"
                      >
                        <ChevronRight size={15} />
                      </button>
                    </div>
                  )}
                  {activePinnedMessage && (
                    <button
                      type="button"
                      onClick={() => handleToggleMessagePin(activePinnedMessage)}
                      className="shrink-0 rounded p-1 text-[#DD0000] transition hover:bg-zinc-100"
                      aria-label="Hiqe fiksimin e këtij mesazhi"
                      title="Hiqe fiksimin"
                    >
                      <Pin size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setDismissedPinnedBannerChatId(selected.id)}
                    className="shrink-0 rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
                    aria-label="Mbylle njoftimin për mesazhin e fiksuar"
                    title="Mbylle njoftimin"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Messages Stream */}
              <div className="relative flex-1 min-h-0">
                <div
                  ref={messagesContainerRef}
                  onScroll={(event) => {
                    const container = event.currentTarget;
                    const atBottom = (
                      container.scrollHeight - container.clientHeight - container.scrollTop <= 48
                    );
                    atMessagesBottomRef.current = atBottom;
                    setIsAtMessagesBottom(atBottom);
                    if (container.scrollTop <= 24) loadOlderMessages();
                  }}
                  className="h-full overflow-y-auto bg-gradient-to-br from-muted/20 via-background to-primary/5 p-4 sm:p-6 custom-scrollbar"
                >
                <div className="flex min-h-full flex-col justify-end gap-3">
                {hasOlderMessages && (
                  <button
                    type="button"
                    onClick={loadOlderMessages}
                    className="mx-auto block rounded-full border border-border bg-card px-3 py-1.5 text-[11px] font-semibold text-muted-foreground shadow-sm transition hover:bg-accent hover:text-foreground"
                  >
                    Shfaq mesazhet më të vjetra ({matchingMessages.length - displayedMessages.length})
                  </button>
                )}
                {isMultiSelectMode && (
                  <div className="sticky top-0 z-20 flex items-center justify-between rounded-xl border border-border bg-card/95 px-3 py-2 shadow-sm backdrop-blur relative">
                    <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                      <CheckSquare size={15} className="text-primary" />
                      {`Të zgjedhura: ${selectedMessageIds.length}`}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={!selectedMessageIds.length}
                        onClick={() => {
                          const selectedMessages = displayedMessages.filter((message) => (
                            selectedMessageIds.includes(message.id)
                          ));
                          if (selectedMessages.length !== selectedMessageIds.length) {
                            setHideMessagesError('Disa mesazhe të zgjedhura nuk janë më të disponueshme.');
                            return;
                          }
                          setTargetDeleteMessages(selectedMessages);
                          setShowDeleteMessageDialog(true);
                        }}
                        className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40"
                      >
                        Fshi
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsMultiSelectMode(false);
                          setSelectedMessageIds([]);
                        }}
                        className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"
                        aria-label="Cancel selecting messages"
                      >
                        <X size={15} />
                      </button>
                    </div>
                    {hideMessagesError && (
                      <p role="alert" className="absolute left-3 top-full mt-1 rounded-lg bg-white px-2 py-1 text-[11px] text-red-600 shadow">
                        {hideMessagesError}
                      </p>
                    )}
                  </div>
                )}
                {pollActionError && (
                  <p role="alert" className="sticky top-0 z-10 mb-2 rounded-xl border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-700 shadow-sm">
                    {pollActionError}
                  </p>
                )}
                {displayedMessages.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center p-8 text-center text-muted-foreground">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary mb-2">
                      {selected.kind === 'notes' ? <BookOpen size={28} /> : <MessageCircle size={28} />}
                    </div>
                    <p className="text-xs font-bold text-foreground">
                      {selected.kind === 'notes' ? 'Këtu janë shënimet tuaja personale' : 'Filloni bisedën në gjermanisht!'}
                    </p>
                    <p className="text-[11px] text-muted-foreground max-w-xs mt-1">
                      {selected.kind === 'notes'
                        ? 'Ruani shënime, fjalor, linqe dhe mendime të rëndësishme.'
                        : 'Komunikim i sigurt, me thirrje zëri/video dhe shkëmbim ushtrimesh.'}
                    </p>
                  </div>
                ) : (
                  displayedMessages.map((message, index) => {
                    const isMe = message.sender_id === (user?.id || 'self');
                    const isNotes = selected.kind === 'notes';
                    const messageReactions = Array.isArray(reactionsMap[message.id])
                      ? reactionsMap[message.id]
                      : Array.isArray(message.reactions) ? message.reactions : [];
                    const reactionCounts = messageReactions.reduce((counts, reaction) => ({
                      ...counts,
                      [reaction.emoji]: (counts[reaction.emoji] || 0) + 1,
                    }), {});
                    const senderProfile = isMe
                      ? user
                      : profiles.find((profile) => profile.id === message.sender_id);
                    const profileDisplayName = getDisplayName(senderProfile, '');
                    const senderDisplayName = isMe
                      ? getDisplayName(user)
                      : profileDisplayName || 'Student';
                    const latestReadAt = isMe
                      ? (messageReadsMap[message.id] || [])
                        .filter((read) => read.user_id !== user?.id && read.read_at)
                        .sort((left, right) => new Date(right.read_at) - new Date(left.read_at))[0]?.read_at
                      : null;
                    const prevMsg = displayedMessages[index - 1];
                    const showDate =
                      !prevMsg ||
                      formatMessageDateGroup(prevMsg.created_at, language) !==
                        formatMessageDateGroup(message.created_at, language);

                    return (
                      <React.Fragment key={message.id}>
                        {showDate && (
                          <div className="my-3 flex items-center justify-center">
                            <span className="rounded-full border border-zinc-300 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-700 shadow-sm">
                              {formatMessageDateGroup(message.created_at, language)}
                            </span>
                          </div>
                        )}

                        <div
                          id={`chat-message-${message.id}`}
                          onClick={() => {
                            if (isMultiSelectMode) toggleSelectedMessage(message.id);
                          }}
                          className={`group relative flex w-full flex-col ${isMe ? 'items-end' : 'items-start'}`}
                        >
                          {isMultiSelectMode && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleSelectedMessage(message.id);
                              }}
                              aria-pressed={selectedMessageIds.includes(message.id)}
                              aria-label="Toggle message selection"
                              className={`mb-1 flex h-6 w-6 items-center justify-center rounded-md border ${selectedMessageIds.includes(message.id) ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-transparent'}`}
                            >
                              <Check size={14} />
                            </button>
                          )}

                          {/* Message row with Avatar */}
                          <div className={`flex w-full max-w-[85%] flex-col sm:max-w-xl ${isMe ? 'items-end' : 'items-start'}`}>
                            <div className={`flex w-full items-end gap-2 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                            {/* Avatar next to bubble */}
                            <ProfileAvatar
                              profile={senderProfile}
                              name={senderDisplayName}
                              size="h-7 w-7"
                            />

                            <div className={`flex min-w-0 flex-1 flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                            {/* Message Bubble (Matching entriks-hr design) */}
                            <div
                              className={`group relative max-w-[78%] min-w-0 rounded-2xl border-0 p-3 shadow-sm transition-all hover:shadow-md ${
                                isMe
                                  ? 'bg-[#FFF4BF] text-zinc-900 rounded-br-md'
                                  : 'bg-white text-zinc-900 rounded-bl-md'
                              }`}
                            >
                              {!isMultiSelectMode && (
                                <div
                                  className={`pointer-events-none absolute top-1/2 z-20 flex -translate-y-1/2 items-center gap-1 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 ${
                                    isMe ? 'right-full mr-1' : 'left-full ml-1'
                                  }`}
                                >
                                  <div className="rounded-full border border-zinc-200/90 bg-white p-0.5 shadow-md">
                                    <MessageActionMenu
                                      onReply={() => {
                                        setEditingMessage(null);
                                        setReplyingTo(message);
                                      }}
                                      onForward={() => {
                                        setForwardingMessage(message);
                                        setShowForwardModal(true);
                                      }}
                                      onCopy={() => handleCopyText(message.content)}
                                      onEdit={!['poll', 'contact'].includes(message.message_type) && Number.isFinite(Number(message.id)) ? () => {
                                        setReplyingTo(null);
                                        setEditingMessage(message);
                                        setNewMessage(message.content || '');
                                      } : undefined}
                                      onPin={!isNotes && Number.isFinite(Number(message.id))
                                        ? () => handleToggleMessagePin(message)
                                        : undefined}
                                      isPinned={Boolean(message.pinned_at)}
                                      onDelete={() => {
                                        setTargetDeleteMessages([message]);
                                        setShowDeleteMessageDialog(true);
                                      }}
                                      onSelect={() => {
                                        setIsMultiSelectMode(true);
                                        setSelectedMessageIds([message.id]);
                                      }}
                                      isMe={isMe}
                                    />
                                  </div>
                                  <div className="flex w-max flex-nowrap items-center gap-0.5 rounded-full border border-zinc-200/90 bg-white px-1 py-0.5 shadow-md">
                                    {QUICK_REACTIONS.map((emo) => {
                                      const hasReacted = messageReactions.some((reaction) => (
                                        reaction.user_id === user?.id && reaction.emoji === emo
                                      ));
                                      return (
                                        <button
                                          key={emo}
                                          type="button"
                                          aria-label={`Reago me ${emo}`}
                                          aria-pressed={hasReacted}
                                          onClick={() => handleToggleReaction(message.id, emo)}
                                          className={`flex h-5 w-5 items-center justify-center rounded-full transition hover:bg-accent ${hasReacted ? 'bg-primary/10' : ''}`}
                                          title={emo}
                                        >
                                          <ReactionEmoji emoji={emo} size={15} />
                                        </button>
                                      );
                                    })}
                                    <MessageReactionMoreButton
                                      onSelect={(emo) => handleToggleReaction(message.id, emo)}
                                      className="h-5 w-5"
                                    />
                                  </div>
                                </div>
                              )}
                              {/* Peer sender name in bold */}
                              {!isMe && !isNotes && (
                                <p className="mb-1 text-xs font-bold text-foreground">
                                  {senderDisplayName}
                                </p>
                              )}

                              {/* Forwarded tag */}
                              {message.forwarded && (
                                <div className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold text-muted-foreground italic">
                                  <Forward size={12} />
                                  <span>Përcjellë</span>
                                </div>
                              )}

                              {/* Reply Quote Block (Matching Screenshot 1) */}
                              {message.replyTo && (
                                <MessageReplyQuote replyTo={message.replyTo} />
                              )}

                              {/* Render Content */}
                              {message.attachment_url && message.attachment_type?.startsWith('image/') && (
                                <a href={message.attachment_url} target="_blank" rel="noreferrer" className="mb-2 block">
                                  <img src={message.attachment_url} alt={message.attachment_name || 'Attachment'} className="max-h-64 max-w-full rounded-xl object-cover" />
                                </a>
                              )}
                              {message.attachment_url &&
                                !message.attachment_type?.startsWith('image/') &&
                                message.message_type !== 'audio' &&
                                !message.attachment_type?.startsWith('audio/') && (
                                <a href={message.attachment_url} target="_blank" rel="noreferrer" className="mb-2 block rounded-xl bg-zinc-100 px-3 py-2 text-xs font-semibold text-zinc-700 underline">
                                  {message.attachment_name || 'Download file'}
                                </a>
                              )}
                              {message.message_type === 'poll' ? (
                                <PollMessage
                                  message={message}
                                  votes={pollVotesMap[message.id] || []}
                                  currentUserId={user?.id}
                                  onVote={handleVoteOnPoll}
                                  isVoting={votingOnPollId === message.id}
                                />
                              ) : message.message_type === 'contact' ? (
                                <ContactMessage message={message} onStartChat={handleStartDirectChat} />
                              ) : (
                                <div data-no-translate="true">
                                  {renderMessageBody(
                                    message.content,
                                    isMe,
                                    message.message_type === 'audio' || message.attachment_type?.startsWith('audio/')
                                      ? message.attachment_url
                                      : null
                                  )}
                                </div>
                              )}

                              {/* Bottom metadata: Time & Read Receipt (✓✓) & Notes icon */}
                              <div
                                className={`mt-1.5 flex items-center justify-end gap-1.5 text-[10px] font-medium ${
                                  isMe ? 'text-muted-foreground' : 'text-muted-foreground'
                                }`}
                              >
                                <span>{formatMessageTime(message.created_at, language)}</span>
                                {message.edited_at && <span>Ndryshuar</span>}

                                {isMe && selected.kind !== 'notes' && (
                                  <MessageReadReceipt
                                    status={message.seen ? 'seen' : 'sent'}
                                  />
                                )}
                              </div>
                            </div>
                            {Object.entries(reactionCounts).length > 0 && (
                              <div className={`mt-1 flex flex-wrap gap-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                {Object.entries(reactionCounts).map(([emoji, count]) => {
                                  const hasReacted = messageReactions.some((reaction) => (
                                    reaction.user_id === user?.id && reaction.emoji === emoji
                                  ));
                                  return (
                                    <button
                                      key={emoji}
                                      type="button"
                                      aria-label={`${emoji}, ${count} reagime`}
                                      aria-pressed={hasReacted}
                                      onClick={() => handleToggleReaction(message.id, emoji)}
                                      className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs shadow-sm transition ${
                                        hasReacted
                                          ? 'border-primary/40 bg-primary/10'
                                          : 'border-border bg-background hover:bg-accent'
                                      }`}
                                    >
                                      <ReactionEmoji emoji={emoji} size={14} />
                                      <span className="text-[10px] font-semibold text-muted-foreground">{count}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            {isMe && selected.kind !== 'notes' &&
                              message.id === latestOutgoingMessageId &&
                              message.seen &&
                              latestReadAt && (
                                <div className="mt-1.5 flex w-full justify-end">
                                  <span className="rounded-full border border-border/50 bg-background px-2.5 py-1 text-[11px] leading-none text-muted-foreground shadow-sm">
                                    {formatReadDate(latestReadAt, language)}
                                  </span>
                                </div>
                              )}
                            </div>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}

                {/* Live Typing bubble (Matching entriks-hr) */}
                {isConversationTyping(selected?.id) && (
                  <TypingIndicator
                    variant="bubble"
                    senderName={selected.title}
                    label="Duke shkruar..."
                  />
                )}

                </div>
                </div>
                {!isAtMessagesBottom && displayedMessages.length > 0 && (
                  <button
                    type="button"
                    onClick={scrollMessagesToBottom}
                    aria-label="Shko te mesazhet më të reja"
                    title="Shko te mesazhet më të reja"
                    className="absolute bottom-4 right-5 z-30 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-lg transition hover:bg-accent"
                  >
                    <ArrowDown size={18} />
                  </button>
                )}
              </div>

              {/* Composer Section */}
              <div className="border-t border-border bg-card p-3.5 relative">
                {editingMessage && (
                  <div className="mb-2 flex items-center justify-between rounded-xl border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs">
                    <span className="font-bold text-primary">Duke ndryshuar mesazhin</span>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingMessage(null);
                        setNewMessage(chatDrafts[selected.id] || '');
                      }}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="Cancel editing message"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                {/* Reply Banner */}
                {replyingTo && (
                  <div className="mb-2 flex min-w-0 items-center gap-2 rounded-xl border border-primary/15 border-l-[3px] border-l-primary bg-primary/5 px-3 py-2">
                    <CornerDownLeft size={15} className="shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[11px] font-bold text-primary">
                        Duke iu përgjigjur: {replyingTo.senderName || 'Përdorues'}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {replyingTo.content || replyingTo.attachment_name || 'Skedar'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setReplyingTo(null)}
                      className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-primary/10 hover:text-foreground"
                      aria-label="Anulo përgjigjen"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}

                {/* Voice Recording Bar */}
                {isRecording ? (
                  <VoiceRecordingBar
                    stream={recordingStream}
                    duration={recordingDuration}
                    onCancel={cancelRecording}
                    onSend={stopAndSendRecording}
                  />
                ) : (
                  /* Composer Input Card */
                  <form
                    onSubmit={handleSendMessage}
                    className="flex items-center gap-2 rounded-2xl border border-input bg-muted/50 px-3 py-1.5 shadow-sm"
                  >
                    <button
                      type="button"
                      onClick={() => setShowFileUploadModal(true)}
                      className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground transition"
                      title="Bashkëngjit skedar ose foto"
                    >
                      <Paperclip size={18} />
                    </button>
                    <button
                      ref={emojiButtonRef}
                      type="button"
                      onClick={() => setShowEmojiPicker((p) => !p)}
                      onMouseDown={(event) => event.preventDefault()}
                      className="shrink-0 rounded-lg bg-muted/60 p-1.5 text-foreground transition hover:bg-accent"
                      title="Zgjidh emotikonë"
                      aria-label="Hap emoji picker"
                      aria-expanded={showEmojiPicker}
                    >
                      <Smile size={19} />
                    </button>
                    <EmojiPickerPopover
                      open={showEmojiPicker}
                      anchorRef={emojiButtonRef}
                      onClose={() => setShowEmojiPicker(false)}
                      onSelectEmoji={handleSelectEmoji}
                    />
                    {selected.kind !== 'notes' && !editingMessage && (
                      <button
                        type="button"
                        onClick={() => {
                          setPollActionError('');
                          setShowPollDialog(true);
                        }}
                        className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-amber-50 hover:text-amber-700"
                        title="Krijo anketë"
                        aria-label="Krijo anketë"
                      >
                        <BarChart3 size={18} />
                      </button>
                    )}
                    <div className="relative min-w-0 flex-1">
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 overflow-hidden"
                      >
                        <div
                          className={`whitespace-pre-wrap break-words py-2 pr-9 text-[13px] leading-5 ${
                            newMessage ? 'text-foreground' : 'text-muted-foreground'
                          }`}
                          style={{ transform: `translateY(-${composerScrollTop}px)` }}
                        >
                          {newMessage ? (
                            <TwemojiText text={newMessage} size={16} disableLinks />
                          ) : (
                            selected.kind === 'notes'
                              ? t('Shkruaj mesazh...')
                              : `${t('Shkruaj mesazh për')} ${selected.title}...`
                          )}
                        </div>
                      </div>
                      <textarea
                        ref={composerInputRef}
                        rows={1}
                        value={newMessage}
                        onChange={(e) => {
                          const value = e.target.value;
                          resizeComposer(e.target);
                          setCurrentDraft(value);
                          if (selected?.id) sendTyping(selected.id, value.length > 0);
                          const cursor = e.target.selectionStart ?? value.length;
                          const activeMention = value.slice(0, cursor).match(/(?:^|\s)@([\w.-]*)$/);
                          setMentionQuery(activeMention ? activeMention[1] : null);
                          setMentionIndex(0);
                        }}
                        onBlur={() => {
                          if (selected?.id) sendTyping(selected.id, false);
                        }}
                        onScroll={(event) => setComposerScrollTop(event.currentTarget.scrollTop)}
                        onKeyDown={(event) => {
                          if (
                            event.key === 'Enter'
                            && !event.shiftKey
                            && !event.nativeEvent.isComposing
                          ) {
                            if (mentionQuery !== null && filteredMentionCandidates.length > 0) {
                              event.preventDefault();
                              handleSelectMention(filteredMentionCandidates[mentionIndex] || filteredMentionCandidates[0]);
                              return;
                            }
                            event.preventDefault();
                            if (newMessage.trim()) handleSendMessage(event);
                            return;
                          }
                          if (mentionQuery === null || filteredMentionCandidates.length === 0) return;
                          if (event.key === 'ArrowDown') {
                            event.preventDefault();
                            setMentionIndex((index) => (index + 1) % filteredMentionCandidates.length);
                          } else if (event.key === 'ArrowUp') {
                            event.preventDefault();
                            setMentionIndex((index) => (index - 1 + filteredMentionCandidates.length) % filteredMentionCandidates.length);
                          } else if (event.key === 'Escape') {
                            setMentionQuery(null);
                          }
                        }}
                        placeholder={selected.kind === 'notes'
                          ? t('Shkruaj mesazh...')
                          : `${t('Shkruaj mesazh për')} ${selected.title}...`}
                        className="max-h-[104px] min-h-9 w-full resize-none overflow-y-hidden bg-transparent py-2 text-[13px] leading-5 text-transparent caret-foreground placeholder:!text-transparent selection:bg-primary/20 selection:text-transparent focus:outline-none"
                      />
                    </div>

                    {newMessage.trim() ? (
                      <button
                        type="submit"
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition shadow-sm"
                      >
                        {editingMessage ? <Check size={15} /> : <Send size={15} />}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={startRecording}
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition shadow-sm"
                        title="Regjistro zë"
                      >
                        <Mic size={17} />
                      </button>
                    )}
                  </form>
                )}

                {mentionQuery !== null && filteredMentionCandidates.length > 0 && (
                  <div className="absolute bottom-[4.75rem] left-12 z-30 w-64 overflow-hidden rounded-2xl border border-zinc-200 bg-white p-1.5 shadow-xl">
                    <p className="px-2.5 py-1.5 text-[10px] font-extrabold uppercase tracking-wide text-zinc-400">Përmend dikë</p>
                    {filteredMentionCandidates.map((profile, index) => {
                      const name = getDisplayName(profile);
                      return (
                        <button
                          key={profile.id}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleSelectMention(profile)}
                          className={`flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left ${index === mentionIndex ? 'bg-amber-50' : 'hover:bg-zinc-50'}`}
                        >
                          <ProfileAvatar profile={profile} name={name} size="h-8 w-8" rounded="rounded-lg" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-xs font-bold text-zinc-900">{name}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

              </div>
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center text-muted-foreground">
              <MessageCircle size={36} className="text-muted-foreground/50 mb-2" />
              <h3 className="text-base font-bold text-foreground">Zgjidhni një bisedë</h3>
            </div>
          )}
        </section>
      </div>

      {/* =========================================================
          MODALS & DIALOGS
         ========================================================= */}
      {showMediaGallery && (
        <ChatModalPortal>
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-zinc-950/55 p-4 backdrop-blur-[2px]"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setShowMediaGallery(false);
            }}
          >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="shared-media-title"
            className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border-0 bg-white text-zinc-950 shadow-2xl"
          >
            <header className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
              <div>
                <h2 id="shared-media-title" className="text-sm font-bold text-zinc-950">Skedarët e ndarë</h2>
                <p className="text-xs text-zinc-600">{selected?.title}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowMediaGallery(false)}
                className="rounded-lg p-2 text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900"
                aria-label="Mbyll dritaren"
                title="Mbyll"
              >
                <X size={16} />
              </button>
            </header>
            <div className="flex gap-1 border-b border-zinc-100 px-4 py-2">
              {[
                ['all', 'Të gjitha'],
                ['images', 'Fotot'],
                ['files', 'Skedarët'],
              ].map(([filter, label]) => (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setMediaGalleryFilter(filter)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${mediaGalleryFilter === filter ? 'bg-amber-50 text-[#DD0000]' : 'text-zinc-600 hover:bg-zinc-100'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {sharedAttachments.length === 0 ? (
                <div className="flex min-h-40 flex-col items-center justify-center gap-2 text-center text-zinc-500">
                  <Paperclip size={22} />
                  <p className="text-xs">Nuk ka skedarë të ndarë në këtë bisedë.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {sharedAttachments.map((attachment) => {
                    const isImage = attachment.attachment_type?.startsWith('image/');
                    return (
                      <a
                        key={attachment.id}
                        href={attachment.attachment_url}
                        target="_blank"
                        rel="noreferrer"
                        className="group overflow-hidden rounded-xl border border-zinc-200 bg-white transition hover:border-amber-300 hover:shadow-sm"
                      >
                        {isImage ? (
                          <img
                            src={attachment.attachment_url}
                            alt={attachment.attachment_name || 'Shared image'}
                            className="h-32 w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-32 items-center justify-center bg-zinc-50 text-zinc-500">
                            <Paperclip size={24} />
                          </div>
                        )}
                        <div className="p-2.5">
                          <p className="truncate text-xs font-semibold text-zinc-900">
                            {attachment.attachment_name || attachment.content || 'Skedar'}
                          </p>
                          <p className="mt-1 text-[10px] text-zinc-500">
                            {formatMessageDateGroup(attachment.created_at, language)}
                          </p>
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
          </div>
        </ChatModalPortal>
      )}
      <ClearConversationDialog
        open={showClearConversationDialog}
        onClose={() => setShowClearConversationDialog(false)}
        onClear={handleClearConversation}
      />
      <MuteConversationDialog
        open={Boolean(muteTargetChat)}
        conversationName={muteTargetChat?.title}
        onClose={() => setMuteTargetChat(null)}
        onMute={(durationSeconds) => muteChat(muteTargetChat.id, durationSeconds)}
        isNotes={selected?.kind === 'notes'}
      />
      <ChatPollDialog
        open={showPollDialog}
        onClose={() => setShowPollDialog(false)}
        onCreate={handleCreatePoll}
        isSubmitting={isCreatingPoll}
      />

      <ForwardMessageModal
        open={showForwardModal}
        onClose={() => {
          setShowForwardModal(false);
          setForwardingMessage(null);
        }}
        message={forwardingMessage}
        conversations={[myNotesChat, ...allChats]}
        onForward={handleForwardMessage}
      />

      <CallPanel
        open={showCallPanel}
        onClose={handleEndCall}
        peerName={activeCall?.fromUid === user?.id
          ? selected?.title
          : activeCall?.fromName || selected?.title || 'Student'}
        peerProfession={activeCall?.isGroup ? 'Grup Mësimor B2' : 'Nxënës Gjermanishteje'}
        isGroup={activeCall?.isGroup || false}
        localMediaStream={callSession.localStream}
        remoteMediaStream={callSession.remoteStream}
        screenMediaStream={callSession.screenStream}
        isMuted={callSession.isMuted}
        isCameraOn={callSession.isCameraOn}
        isScreenSharing={callSession.isScreenSharing}
        onToggleMute={callSession.toggleMute}
        onToggleCamera={callSession.toggleCamera}
        onToggleScreenShare={callSession.toggleScreenShare}
        callError={callSession.callError}
      />

      {incomingCall && (
        <IncomingCallPopup
          callerName={incomingCall.fromName || incomingCall.peerName || 'Student'}
          callerProfession="Nxënës Gjermanishteje"
          onAccept={() => {
            const callChat = allChats.find((chat) => chat.id === incomingCall.conversationId);
            if (callChat) {
              setSelected(callChat);
              setMobileShowList(false);
            }
            setShowCallPanel(true);
            void callSession.acceptIncomingCall();
          }}
          onReject={callSession.rejectIncomingCall}
        />
      )}

      <DeleteMessageDialog
        open={showDeleteMessageDialog}
        onClose={() => {
          setShowDeleteMessageDialog(false);
          setTargetDeleteMessages([]);
        }}
        onDeleteForMe={handleHideDeleteTargetsForMe}
        onDeleteForEveryone={() => (
          targetDeleteMessages.length > 1
            ? handleDeleteMessagesForEveryone(targetDeleteMessages)
            : handleDeleteMessage(targetDeleteMessages[0])
        )}
        canDeleteForEveryone={
          selected?.kind !== 'notes' &&
          targetDeleteMessages.length > 0 &&
          targetDeleteMessages.every((message) => message.sender_id === user?.id)
        }
        messageCount={targetDeleteMessages.length || 1}
      />

      <FileUploadModal
        open={showFileUploadModal}
        onClose={() => setShowFileUploadModal(false)}
        onSendFiles={(files, caption) => {
          Promise.all(files.map(async (file) => {
            const path = `${user.id}/${selected.id}/${Date.now()}-${file.name}`;
            const { error: uploadError } = await supabase.storage.from('chat-attachments').upload(path, file, {
              upsert: false,
              contentType: file.type,
            });
            if (uploadError) throw uploadError;
            const { data: urlData } = supabase.storage.from('chat-attachments').getPublicUrl(path);
            const content = caption ? `${file.name} - ${caption}` : file.name;
            if (selected.kind === 'notes') {
              const fileMsg = {
                id: `note_file_${Date.now()}_${Math.random()}`,
                conversation_id: 'notes_me',
                sender_id: user.id,
                senderName: getDisplayName(user),
                content,
                attachment_url: urlData.publicUrl,
                attachment_name: file.name,
                attachment_type: file.type,
                attachment_size: file.size,
                created_at: new Date().toISOString(),
                seen: true,
              };
              saveNotesMessages([...notesMessages, fileMsg]);
              setMessages((previous) => [...previous, fileMsg]);
              return;
            }
            const { data, error } = await supabase.from('messages').insert({
              conversation_id: selected.id,
              sender_id: user.id,
              sender_name: getDisplayName(user),
              content,
              message_type: 'file',
              attachment_url: urlData.publicUrl,
              attachment_name: file.name,
              attachment_type: file.type,
              attachment_size: file.size,
            }).select('id, conversation_id, sender_id, sender_name, content, timestamp, message_type, attachment_url, attachment_name, attachment_type, attachment_size').single();
            if (error) throw error;
            const savedMessage = { ...data, created_at: data.timestamp, senderName: data.sender_name, seen: false };
            setChatMessagesMap((previous) => ({ ...previous, [selected.id]: [...(previous[selected.id] || []), savedMessage] }));
            setMessages((previous) => [...previous, savedMessage]);
          })).catch((error) => console.error('Unable to send attachment:', error));
          setShowFileUploadModal(false);
        }}
      />

      <NewChatModal
        open={showNewChatModal}
        onClose={() => setShowNewChatModal(false)}
        users={profiles}
        loadError={profilesLoadError}
        currentUserId={user?.id}
        onlineUserIds={onlineUserIds}
        onCreateGroup={handleCreateGroup}
        onStartDirectChat={handleStartDirectChat}
      />

      <UserProfileDialog
        open={showUserProfileDialog}
        onClose={() => setShowUserProfileDialog(false)}
        userProfile={selected?.peer}
        isOnline={isUserOnline(selected?.peer?.id)}
        onStartChat={() => setShowUserProfileDialog(false)}
        onStartCall={() => {
          setShowUserProfileDialog(false);
          handleStartCall();
        }}
      />
      <GroupInfoDialog
        open={showGroupInfoDialog && selected?.kind === 'group'}
        onClose={() => setShowGroupInfoDialog(false)}
        group={selected}
        members={selected?.members || []}
        allUsers={profiles}
        currentUserId={user?.id}
        canAddMembers={selected?.created_by === user?.id}
        onAddMember={handleAddGroupMember}
        onLeaveGroup={handleLeaveGroup}
      />
    </DashboardLayout>
  );
};

export default ChatPage;
